<template>
  <v-dialog
      v-model="show"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      @keydown.esc="close"
  >
    <v-card class="live-editor-wrapper" tile>
      <!-- HEADER -->

      <!-- BODY -->
      <div class="editor-body">
        <!-- LEFT: CANVAS AREA -->
        <div class="canvas-container" ref="canvasRef">
          <!-- Kopfzeile als schwebende Pille: mehr Fläche fürs Artwork -->
          <div class="le-topbar">
            <button type="button" class="le-topbar__close" title="Schließen (Esc)" @click="close">
              <v-icon small>mdi-close</v-icon>
            </button>
            <span v-if="taskCountryFlag" class="le-topbar__flag" :title="taskCountryCode">{{ taskCountryFlag }}</span>
            <span class="le-topbar__name">{{ orderName }}</span>
            <span class="le-topbar__meta">{{ configMaterial }} · {{ effectiveOrientation }} · {{ configSize }}</span>
          </div>
          <!-- Upscale-Kette: öffnet den Vergleichsdialog direkt bei den Gesichtern (Vorher / Nachher) -->
          <button
              v-if="compareFaceRegions.length"
              type="button"
              class="upscale-faces-toggle"
              title="Gesichter vorher / nachher vergleichen"
              @click="openUpscaleFace(null)"
          >
            <v-icon small>mdi-face-recognition</v-icon>
            Vorher / Nachher
            <span class="upscale-faces-toggle__count">{{ compareFaceRegions.length }}</span>
          </button>
          <div class="canvas-inner">
            <div
                @click="() => (dialogImage = artworkDisplayUrl)"
                class="zoom-hint d-flex align-center"
            >
              <v-icon small>mdi-alpha-y-box-outline</v-icon>
              =
              <v-icon small>mdi-loupe</v-icon>
            </div>
            <!-- FRAME -->
            <div
                ref="frameRef"
                class="print-frame elevation-10"
                :class="{ 'print-frame--drag-over': isFrameDragOver }"
                :style="frameContainerStyle"
                @mousedown.prevent="startArtworkDrag"
                @wheel.prevent="onWheel"
                @dragover.prevent="onFrameDragOver"
                @dragenter.prevent="onFrameDragEnter"
                @dragleave.prevent="onFrameDragLeave"
                @drop.prevent="onFrameDrop"
            >
              <!-- Artwork -->
              <!-- Passepartout-Ausschnitt -->
              <div class="artwork-clip" :style="artworkClipStyle">
                <!-- Textur-Hintergrund (transparente Artworks): als Quadrat
                     zentriert, bei Bedarf 90° gedreht — Maserung folgt der
                     Artwork-Ausrichtung (Hochformat → vertikal) -->
                <img
                    v-if="backgroundLayerTextureUrl"
                    :src="backgroundLayerTextureUrl"
                    class="bg-texture-layer"
                    :style="backgroundLayerTextureStyle"
                    draggable="false"
                />
                <img
                    :key="task.updateKey"
                    v-if="artworkDisplayUrl"
                    :src="artworkDisplayUrl"
                    class="artwork-layer"
                    :style="artworkImageStyle"
                    @load="onImageLoad"
                    draggable="false"
                />
                <div v-else class="loading-placeholder">
                  <v-progress-circular indeterminate color="grey"></v-progress-circular>
                </div>
                <!-- Bereich markieren: Rechteck auf dem Artwork ziehen (Region-Edit) -->
                <div
                    v-if="canAiEdit && regionDrawMode && imageLayout"
                    ref="regionDrawLayer"
                    class="artwork-layer region-draw-layer"
                    :style="artworkImageStyle"
                    @mousedown.stop="onRegionDrawDown"
                    @mousemove="onRegionDrawMove"
                    @mouseup.stop="onRegionDrawUp"
                >
                  <div v-if="regionDrawStyle" class="region-draw-box" :style="regionDrawStyle"></div>
                </div>
                <!-- Regionen der Upscale-Kette: Rahmen + Score (liegt exakt auf dem Artwork) -->
                <div
                    v-if="canAiEdit && imageLayout && !regionDrawMode && ((showUpscaleRegions && upscaleRegionBoxes.length) || regionEditRunningBoxes.length || customRegionBoxStyle)"
                    class="artwork-layer upscale-region-overlay"
                    :style="artworkImageStyle"
                >
                  <!-- Bereich, der gerade neu gerendert wird -->
                  <div v-if="customRegionBoxStyle" class="upscale-region-box upscale-region-box--custom" :style="customRegionBoxStyle"></div>
                  <div v-for="run in regionEditRunningBoxes" :key="'run-' + run.key" class="upscale-region-box upscale-region-box--running" :style="run.style">
                    <span>wird gerendert …</span>
                  </div>
                  <div
                      v-for="box in (showUpscaleRegions ? upscaleRegionBoxes : [])"
                      :key="'upbox-' + box.id"
                      class="upscale-region-box"
                      :class="{ 'upscale-region-box--hidden': box.dashed, 'upscale-region-box--hovered': box.hovered, 'upscale-region-box--clickable': box.clickable }"
                      :style="box.style"
                      :title="box.warning ? 'Prüfen: ' + box.warning : box.clickable ? 'Vorher / Nachher dieser Region ansehen' : ''"
                      @mousedown.stop
                      @click.stop="box.clickable && openUpscaleFace(box.id)"
                  >
                    <span :style="{ background: box.color }">{{ box.text }}</span>
                  </div>
                </div>
              </div>

              <!-- Draggable Texts -->
              <div
                  v-for="(element, idx) in localTextElements"
                  :key="'text-' + idx"
                  class="text-layer"
                  :style="getTextStyle(element, idx)"
                  @mousedown.stop.prevent="startTextDrag($event, idx)"
                  @click.stop="onTextClick(idx)"
              >
                <!-- SVG-Wolke hinter dem Text - identisch zur Render-Logik -->
                <svg
                    v-if="element.bgEnabled && textBoxes[idx] && textBoxes[idx].w > 0"
                    class="text-cloud-svg"
                    :width="cloudDims(idx).rx * 2"
                    :height="cloudDims(idx).ry * 2"
                    :viewBox="`0 0 ${cloudDims(idx).rx * 2} ${cloudDims(idx).ry * 2}`"
                >
                  <defs>
                  <radialGradient :id="`cloud-${uid}-${idx}`" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" :stop-color="cloudColor(element)" :stop-opacity="cloudAlpha(element).core" />
                    <stop offset="20%" :stop-color="cloudColor(element)" :stop-opacity="cloudAlpha(element).core" />
                    <stop offset="45%" :stop-color="cloudColor(element)" :stop-opacity="cloudAlpha(element).mid" />
                    <stop offset="75%" :stop-color="cloudColor(element)" :stop-opacity="cloudAlpha(element).mid * 0.4" />
                    <stop offset="100%" :stop-color="cloudColor(element)" :stop-opacity="0" />
                  </radialGradient>
                  <filter :id="`cloud-${uid}-${idx}-blur`" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur :stdDeviation="(frameRect.width || 500) * 0.015" />
                  </filter>
                  </defs>
                  <ellipse
                      :cx="cloudDims(idx).rx"
                      :cy="cloudDims(idx).ry"
                      :rx="cloudDims(idx).rx"
                      :ry="cloudDims(idx).ry"
                      :fill="`url(#cloud-${uid}-${idx})`"
                      :filter="`url(#cloud-${uid}-${idx}-blur)`"
                  />
                </svg>
                <span :ref="'textMeasure' + idx" class="text-measure">{{ element.text }}</span>
              </div>

              <!-- Grid -->
              <div class="grid-lines"></div>
            </div>

            <!-- Floating Controls (unter dem Frame) -->
            <div class="floating-controls">
              <div class="fc-zoom">
                <!-- Regler erscheint bei Hover über der Zoom-Gruppe -->
                <div class="fc-zoom__pop">
                  <div class="fc-zoom__popInner">
                    <input
                        type="range"
                        class="fc-zoom__range"
                        :min="zoomMin"
                        :max="zoomMax"
                        step="0.01"
                        :value="localCanvas.scale || 1"
                        @input="onSliderZoom($event.target.value)"
                    />
                  </div>
                </div>
                <button type="button" class="fc-zoom__btn" title="Verkleinern" :disabled="localCanvas.scale <= zoomMin" @click="stepZoom(-0.1)"><v-icon small>mdi-minus</v-icon></button>
                <button type="button" class="fc-zoom__val" title="Zoom zurücksetzen" @click="onSliderZoom(1)">{{ (localCanvas.scale * 100).toFixed(0) }}%</button>
                <button type="button" class="fc-zoom__btn" title="Vergrößern (auch Mausrad über dem Bild)" :disabled="localCanvas.scale >= zoomMax" @click="stepZoom(0.1)"><v-icon small>mdi-plus</v-icon></button>
              </div>
              <div class="fc-sep"></div>
              <div class="fc-row fc-row--versions">
                <!-- Versions-Timeline: chronologisch alt → neu (Edits und
                     Refinement zeitstempel-sortiert, z. B. Edit 1 → Refinement
                     → Edit 2); Pfeile zeigen die Entstehungs-Reihenfolge,
                     ganz rechts = neueste Version -->
                <div class="le-version-bar" ref="versionBar">
                  <template v-for="(item, i) in versionBarItems">
                    <v-icon
                        v-if="item.arrow"
                        :key="'le-varr-' + i"
                        x-small
                        class="le-version-arrow"
                    >mdi-chevron-right</v-icon>
                    <button
                        v-else
                        :key="item.key"
                        type="button"
                        class="le-version-chip"
                        :class="{
                          active: selectedVariant === item.key,
                          disabled: item.disabled,
                          pending: item.pending,
                        }"
                        :title="item.title || ''"
                        :disabled="item.disabled"
                        @click="!item.disabled && (selectedVariant = item.key)"
                        @contextmenu="onVersionChipContextMenu($event, item)"
                    >
                      <v-progress-circular v-if="item.pending" indeterminate size="10" width="2" class="mr-1" />
                      {{ item.label }}
                      <v-icon v-if="item.releasedIcon" x-small class="ml-1" title="Für alle freigegeben">mdi-eye-check-outline</v-icon>
                      <span v-if="item.previewIcon" class="le-version-mail" :title="previewSentTitle"><v-icon>mdi-email-outline</v-icon></span>
                    </button>
                  </template>
                </div>
              </div>
              <div class="fc-sep"></div>
              <button type="button" class="fc-zoom__btn" title="Artwork herunterladen" @click="downloadArtwork"><v-icon small>mdi-download</v-icon></button>

              <!-- Rechtsklick auf einen Edit: Freigabe für Nutzer ohne AI_EDIT -->
              <v-menu
                  v-model="releaseMenu.show"
                  :position-x="releaseMenu.x"
                  :position-y="releaseMenu.y"
                  absolute
                  offset-y
              >
                <v-list dense>
                  <v-list-item @click="toggleReleaseEdit">
                    <v-list-item-icon class="mr-3">
                      <v-icon small>{{ releaseMenuEdit && releaseMenuEdit.released ? 'mdi-eye-off-outline' : 'mdi-eye-check-outline' }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>
                      {{ releaseMenuEdit && releaseMenuEdit.released ? 'Freigabe zurückziehen' : 'Freigeben (für alle sichtbar)' }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>
        </div>

        <!-- RIGHT: SIDEBAR -->
        <div class="settings-sidebar">
          <div class="sidebar-inner le-sidebar-pad">
            <!-- Segment-Tabs: Übersicht (Previews/KI) | Design (Text/Artwork) -->
            <div class="sidebar-tabs">
              <button
                  type="button"
                  class="sidebar-tabs__tab"
                  :class="{ 'sidebar-tabs__tab--active': sidebarTab === 'overview' }"
                  @click="sidebarTab = 'overview'"
              >
                <v-icon x-small>mdi-view-dashboard-outline</v-icon> Übersicht
              </button>
              <button
                  type="button"
                  class="sidebar-tabs__tab"
                  :class="{ 'sidebar-tabs__tab--active': sidebarTab === 'design' }"
                  @click="sidebarTab = 'design'"
              >
                <v-icon x-small>mdi-format-text</v-icon> Text &amp; Artwork
                <!-- KI-Textvorschlag vorhanden: Hinweis-Punkt -->
                <span v-if="aiConfigProposal" class="sidebar-tabs__dot" :title="aiProposalActive ? 'KI-Vorschlag aktiv' : 'KI-Vorschlag verfügbar'"></span>
              </button>
            </div>
            <!-- PREVIEWS -->
            <div class="sidebar-inner-settings sidebar-scroll">
              <div v-show="sidebarTab === 'overview'">
              <div
                  v-if="task.revisionComment"
                  class="preview-box preview-box--warning mb-2"
              >
                <div class="text-caption grey--text mb-1">
                  Revision Comment / {{ task.revisionComment.userName }} /
                  {{ getDateFromNow(task.revisionComment.timestamp) }}
                </div>
                <span>{{ task.revisionComment.comment || task.revisionComment }}</span>
              </div>
              <!-- Order-Kommentar des Kunden: sichtbar + kopierbar + als KI-Prompt nutzbar -->
              <div v-if="task.orderNote" class="preview-box preview-box--note le-card">
                <div class="text-caption grey--text mb-1 d-flex align-center">
                  <v-icon x-small class="mr-1">mdi-message-text-outline</v-icon>
                  Order-Kommentar
                  <v-spacer />
                  <v-btn x-small icon :title="orderNoteCopied ? 'Kopiert!' : 'Kopieren'" @click="copyOrderNote">
                    <v-icon x-small>{{ orderNoteCopied ? "mdi-check" : "mdi-content-copy" }}</v-icon>
                  </v-btn>
                  <v-btn v-if="canAiEdit" x-small icon title="Als KI-Prompt übernehmen" @click="aiEditPrompt = task.orderNote">
                    <v-icon x-small>mdi-auto-fix</v-icon>
                  </v-btn>
                </div>
                <span class="order-note-text">{{ task.orderNote }}</span>
              </div>

              <!-- Kunden-Feedback (Bewertung): Thread, Default Übersetzung -->
              <div
                  v-if="task.feedback && task.feedback.hasComment"
                  class="preview-box preview-box--note le-card"
                  :class="task.feedback.satisfied === false ? 'preview-box--bad' : 'preview-box--good'"
              >
                <div class="text-caption grey--text mb-1 d-flex align-center">
                  <v-icon
                      x-small
                      class="mr-1"
                      :color="task.feedback.satisfied === false ? '#d92c2c' : '#3f9d68'"
                  >
                    {{ task.feedback.satisfied === false ? "mdi-thumb-down" : "mdi-thumb-up" }}
                  </v-icon>
                  Kunden-Feedback
                  <v-spacer />
                  <button
                      v-if="feedbackHasOriginals"
                      type="button"
                      class="le-orig-toggle"
                      :class="{ 'le-orig-toggle--on': feedbackShowOriginal }"
                      :title="feedbackShowOriginal ? 'Übersetzung zeigen' : 'Originaltext zeigen'"
                      @click="feedbackShowOriginal = !feedbackShowOriginal"
                  >
                    <v-icon x-small class="mr-1">mdi-translate</v-icon>{{ feedbackShowOriginal ? "Übersetzung" : "Original" }}
                  </button>
                  <v-btn
                      v-if="canAiEdit"
                      x-small
                      icon
                      title="Als KI-Prompt übernehmen"
                      @click="aiEditPrompt = latestFeedbackComment"
                  >
                    <v-icon x-small>mdi-auto-fix</v-icon>
                  </v-btn>
                </div>
                <FeedbackThread :feedback="task.feedback" hide-toggle :show-original="feedbackShowOriginal" />
              </div>

              <!-- KI-Änderung: Prompt -> gpt-image-2-Edit -> 4K-Ultra-Upscale -->
              <!-- KI-Änderung: ganzes Bild (Prompt → 4K-Edit) oder markierter Bereich (Region-Edit).
                   Mit markiertem Bereich wechselt die Box in den Bereichs-Modus: Rahmen, Chip, Typ, Eingaben. -->
              <div v-if="canAiEdit" class="preview-box ai-edit-box le-card" :class="{ 'ai-edit-box--region': customRegion }">
                <div class="text-caption grey--text mb-1 d-flex align-center">
                  <v-icon x-small class="mr-1">{{ customRegion ? "mdi-selection-drag" : "mdi-auto-fix" }}</v-icon>
                  {{ customRegion ? "KI-Änderung für markierten Bereich" : "KI-Änderung" }}
                  <v-spacer />
                  <span v-if="!customRegion && aiEdits.length" class="ai-edit-box__count">{{ aiEdits.length }}</span>
                  <span v-if="regionEditRunning" class="ai-edit-box__count"><v-progress-circular indeterminate size="10" width="2" class="mr-1" />{{ regionEditRunningCount > 1 ? `${regionEditRunningCount} Bereiche laufen` : "Bereich läuft" }}</span>
                </div>

                <!-- Bereichs-Modus: was ist markiert -->
                <template v-if="customRegion">
                  <div class="ai-edit-box__regionRow">
                    <div class="ai-edit-box__thumb" :style="customRegionThumbStyle" title="Markierter Bereich im aktuellen Upscale"></div>
                    <div class="ai-edit-box__regionFields">
                      <select v-model="customRegion.type" class="ai-edit-box__select" title="Was ist in dem Bereich?">
                        <option v-for="t in regionTypes" :key="t.value" :value="t.value">{{ t.label }}</option>
                      </select>
                      <input v-model="customRegion.label" type="text" class="ai-edit-box__name" placeholder="Name (optional)" />
                    </div>
                    <button type="button" class="ai-edit-box__close" title="Markierung verwerfen" @click="cancelCustomRegion"><v-icon small>mdi-close</v-icon></button>
                  </div>
                  <v-textarea
                      v-model="customRegion.instruction"
                      :disabled="regionEditBusy"
                      rows="2"
                      auto-grow
                      dense
                      hide-details
                      solo
                      flat
                      background-color="#fff"
                      placeholder="Was soll in diesem Bereich anders werden? Optional, z. B. 'Nummernschild blank' oder 'Augen offen wie im Foto'"
                      class="ai-edit-box__input"
                      @paste.native="onAiEditPaste"
                      @drop.native.prevent="onAiEditDrop"
                      @dragover.native.prevent
                  />
                  <!-- Referenzbilder: Strg+V im Textfeld oder hierher ziehen (max. 3), gehen als weitere Bilder ans Modell -->
                  <div class="ai-edit-box__refs" :class="{ 'ai-edit-box__refs--empty': !aiEditRefs.length }" @dragover.prevent @drop.prevent="onAiEditDrop">
                    <div v-for="(r, i) in aiEditRefs" :key="i" class="ai-edit-box__ref" :title="r.name">
                      <img :src="r.preview" alt="" />
                      <button type="button" class="ai-edit-box__refRemove" title="Entfernen" @click="removeAiEditRef(i)">×</button>
                    </div>
                    <label class="ai-edit-box__refAdd" :title="aiEditRefs.length ? 'Weiteres Referenzbild' : 'Referenzbild einfügen: Strg+V im Textfeld, hierher ziehen oder klicken'">
                      <v-icon x-small>mdi-image-plus</v-icon>
                      <span v-if="!aiEditRefs.length">Bild: Strg+V · Drag &amp; Drop</span>
                      <input type="file" accept="image/*" multiple hidden @change="(e) => { addAiEditRefFiles(e.target.files); e.target.value = ''; }" />
                    </label>
                  </div>

                  <div class="ai-edit-box__sends">
                    <span class="ai-edit-box__sendLabel">Mitgeschickt:</span>
                    <button v-if="editBaseVariant" type="button" class="ai-edit-box__send ai-edit-box__send--version" :class="{ 'ai-edit-box__send--on': customRegion.inputs.selected }" :title="`Die in der Leiste gewählte Version (${editBaseVariant.label}) als Grundlage: der Bereich wird darauf gerendert und das Ergebnis wird eine neue Version auf dieser Basis`" @click="toggleCustomBase('selected')"><v-icon x-small>{{ customRegion.inputs.selected ? "mdi-check" : "mdi-plus" }}</v-icon>Gewählte Version · {{ editBaseVariant.label }}</button>
                    <template v-if="hasPlainUpscale">
                      <button type="button" class="ai-edit-box__send" :class="{ 'ai-edit-box__send--on': customRegion.inputs.current }" title="Aktueller Stand nach Detail-Pass bzw. Edits. Überarbeitung oder Upscale bleibt immer gewählt." @click="toggleCustomBase('current')"><v-icon x-small>{{ customRegion.inputs.current ? "mdi-check" : "mdi-plus" }}</v-icon>Überarbeitung</button>
                      <button type="button" class="ai-edit-box__send" :class="{ 'ai-edit-box__send--on': customRegion.inputs.upscale }" title="Reiner Upscale vor dem Detail-Pass. Mit Überarbeitung zusammen: zusätzliche Vorlage, allein: wird neu gezeichnet." @click="toggleCustomBase('upscale')"><v-icon x-small>{{ customRegion.inputs.upscale ? "mdi-check" : "mdi-plus" }}</v-icon>Upscale</button>
                    </template>
                    <span v-else class="ai-edit-box__send ai-edit-box__send--on ai-edit-box__send--fixed" title="Immer dabei: der markierte Bereich aus dem aktuellen Upscale"><v-icon x-small>mdi-check</v-icon>Upscale-Ausschnitt</span>
                    <button type="button" class="ai-edit-box__send" :class="{ 'ai-edit-box__send--on': customRegion.inputs.photo }" :disabled="!hasCustomerPhoto" :title="hasCustomerPhoto ? 'Dieselbe Stelle aus dem Kundenfoto mitschicken (Vorlage für Gesicht, Buchstaben, Details)' : 'Kein Kundenfoto vorhanden'" @click="customRegion.inputs.photo = !customRegion.inputs.photo"><v-icon x-small>{{ customRegion.inputs.photo ? "mdi-check" : "mdi-plus" }}</v-icon>Kundenfoto</button>
                    <button type="button" class="ai-edit-box__send" :class="{ 'ai-edit-box__send--on': customRegion.inputs.artwork }" title="Komplettes Artwork als Stilvorlage mitschicken" @click="customRegion.inputs.artwork = !customRegion.inputs.artwork"><v-icon x-small>{{ customRegion.inputs.artwork ? "mdi-check" : "mdi-plus" }}</v-icon>Artwork als Stilvorlage</button>
                  </div>
                  <v-btn color="#7b5cd6" dark small block depressed class="mt-2" :loading="regionEditBusy" :disabled="regionEditBusy" @click="submitCustomRegion">
                    <v-icon left small>mdi-autorenew</v-icon>
                    Bereich neu rendern
                  </v-btn>
                </template>

                <!-- Ganzes Bild -->
                <template v-else-if="canAiEdit">
                  <v-textarea
                      v-model="aiEditPrompt"
                      :disabled="aiEditBusy"
                      rows="1"
                      auto-grow
                      dense
                      hide-details
                      solo
                      flat
                      background-color="#fff"
                      placeholder="Was soll geändert werden? z. B. 'Entferne die Person im Hintergrund'"
                      class="ai-edit-box__input"
                      @paste.native="onAiEditPaste"
                      @drop.native.prevent="onAiEditDrop"
                      @dragover.native.prevent
                  />
                  <!-- Referenzbilder: Strg+V im Textfeld oder hierher ziehen (max. 3), gehen als weitere Bilder ans Modell -->
                  <div class="ai-edit-box__refs" :class="{ 'ai-edit-box__refs--empty': !aiEditRefs.length }" @dragover.prevent @drop.prevent="onAiEditDrop">
                    <div v-for="(r, i) in aiEditRefs" :key="i" class="ai-edit-box__ref" :title="r.name">
                      <img :src="r.preview" alt="" />
                      <button type="button" class="ai-edit-box__refRemove" title="Entfernen" @click="removeAiEditRef(i)">×</button>
                    </div>
                    <label class="ai-edit-box__refAdd" :title="aiEditRefs.length ? 'Weiteres Referenzbild' : 'Referenzbild einfügen: Strg+V im Textfeld, hierher ziehen oder klicken'">
                      <v-icon x-small>mdi-image-plus</v-icon>
                      <span v-if="!aiEditRefs.length">Bild: Strg+V · Drag &amp; Drop</span>
                      <input type="file" accept="image/*" multiple hidden @change="(e) => { addAiEditRefFiles(e.target.files); e.target.value = ''; }" />
                    </label>
                  </div>

                  <div v-if="aiEditError" class="ai-edit-box__error">{{ aiEditError }}</div>
                  <!-- Freier Text: dieselbe Einordnung wie der Auto-Edit (Farbe/Ton, Bereich, Detail mit geschützten Gesichtern) -->
                  <v-btn
                      v-if="regionEditAvailable"
                      color="primary"
                      small
                      block
                      depressed
                      :loading="smartClassifying || smartEditBusy || feedbackEditRunning"
                      :disabled="!aiEditPrompt || aiEditPending || regionEditRunning"
                      class="mt-1"
                      @click="runSmartEdit(aiEditPrompt)"
                  >
                    <v-icon left small>mdi-creation</v-icon>
                    Änderung generieren
                  </v-btn>
                  <v-btn
                      v-else
                      color="primary"
                      small
                      block
                      depressed
                      class="mt-1"
                      :loading="aiEditBusy"
                      :disabled="!aiEditPrompt || aiEditPending"
                      @click="runAiEdit"
                  >
                    <v-icon left small>mdi-creation</v-icon>
                    Änderung generieren (4K)
                  </v-btn>
                  <!-- Änderungen der gewählten Version (Edit 1 … N): Ausschnitt, Wunsch, Score, zurücknehmen -->
                  <div v-if="jobEditTiles.length" class="ai-edit-box__edits">
                    <div class="text-caption grey--text mb-1">In dieser Version enthalten</div>
                    <div v-for="tile in jobEditTiles" :key="'jobedit-' + tile.index" class="ai-edit-box__edit">
                      <div class="ai-edit-box__editThumb" :class="{ 'ai-edit-box__editThumb--full': tile.wholeImage }">
                        <img v-if="tile.thumb" :src="tile.thumb" alt="" />
                      </div>
                      <div class="ai-edit-box__editBody">
                        <div class="ai-edit-box__editTitle">
                          <span v-if="tile.warning" class="ai-edit-box__editWarn" :title="'Prüfen: ' + tile.warning"><v-icon x-small>mdi-alert</v-icon></span>
                          {{ tile.title }}
                          <span v-if="tile.score !== null" class="ai-edit-box__editScore">{{ tile.scoreBefore !== null ? tile.scoreBefore + " → " : "" }}{{ tile.score }}</span>
                        </div>
                        <div v-if="tile.instruction" class="ai-edit-box__editText" :title="tile.instruction">„{{ tile.instruction }}"</div>
                      </div>
                      <button
                          type="button"
                          class="ai-edit-box__editDelete"
                          :disabled="regionEditBusy || regionEditRunning || feedbackEditRunning"
                          :title="tile.index === regionEdits.length - 1 ? 'Diesen Edit zurücknehmen' : 'Diesen und alle späteren Edits zurücknehmen'"
                          @click="removeJobEdit(tile.index)"
                      >
                        <v-icon small>mdi-delete-outline</v-icon>
                      </button>
                    </div>
                  </div>
                  <div v-if="smartClassifying" class="text-caption grey--text mt-1 text-center">Wunsch wird eingeordnet: Text oder Bild…</div>
                  <div v-else-if="feedbackEditRunning" class="text-caption grey--text mt-1 text-center">
                    {{ upscaleJob && upscaleJob.step ? upscaleJob.step : "Wunsch wird eingeordnet und angewendet…" }}
                  </div>
                  <div v-else-if="feedbackEditRecent && feedbackEditState.state === 'done' && feedbackEditLabel" class="ai-edit-box__status">
                    <v-icon x-small color="#2e9e5b">mdi-check-circle</v-icon> {{ feedbackEditLabel }}
                  </div>
                  <div v-else-if="feedbackEditRecent && feedbackEditState.state === 'failed'" class="ai-edit-box__error">{{ feedbackEditLabel }}</div>
                  <div v-if="smartEditUnclear" class="ai-edit-box__unclear">
                    <div>Die KI konnte „{{ smartEditUnclear.prompt }}" keinem Bereich, keiner Farbe und keinem Detail zuordnen{{ smartEditUnclear.reason ? `: ${smartEditUnclear.reason}` : "" }}.</div>
                    <v-btn x-small text color="primary" class="mt-1" :loading="aiEditBusy" :disabled="aiEditPending" @click="aiEditPrompt = smartEditUnclear.prompt; smartEditUnclear = null; runAiEdit()">
                      <v-icon left x-small>mdi-image-refresh-outline</v-icon>
                      Trotzdem ganzes Bild neu generieren (4K)
                    </v-btn>
                  </div>
                  <div v-if="aiEditPending" class="text-caption grey--text mt-1 text-center">
                    Bearbeitung läuft im Hintergrund
                  </div>
                </template>

                <!-- Laufende Bereichs-Edits (mehrere parallel): Ausschnitt + Name + Status -->
                <div v-if="runningEditTiles.length" class="ai-edit-box__edits ai-edit-box__edits--running">
                  <div class="text-caption grey--text mb-1">In Bearbeitung</div>
                  <div v-for="tile in runningEditTiles" :key="'running-' + tile.key" class="ai-edit-box__edit">
                    <div class="ai-edit-box__editThumb" :style="tile.thumbStyle"></div>
                    <div class="ai-edit-box__editBody">
                      <div class="ai-edit-box__editTitle">{{ tile.label }}</div>
                      <div class="ai-edit-box__editText"><v-progress-circular indeterminate size="10" width="2" class="mr-1" />{{ tile.state }}</div>
                    </div>
                  </div>
                </div>

                <!-- Bereich markieren: ein Knopf, der Rest passiert in der Box -->
                <template v-if="regionEditAvailable && !customRegion">
                  <v-btn
                      small
                      block
                      text
                      class="mt-0 ai-edit-box__drawBtn"
                      :class="{ 'ai-edit-box__drawBtn--on': regionDrawMode }"
                      :disabled="regionEditBusy"
                      @click="toggleRegionDraw"
                  >
                    <v-icon left small>{{ regionDrawMode ? "mdi-close" : "mdi-selection-drag" }}</v-icon>
                    {{ regionDrawMode ? "Markieren abbrechen" : "Nur einen Bereich ändern" }}
                  </v-btn>
                  <div v-if="regionDrawMode" class="ai-edit-box__drawHint">Rechteck auf dem Artwork ziehen</div>
                </template>
                <div v-if="regionEditState && regionEditState.state === 'failed' && !regionEditRunning" class="ai-edit-box__error">Letzter Bereichs-Edit fehlgeschlagen: {{ regionEditState.error }}</div>
              </div>
              <UpscaleRegionsPanel
                  v-if="canAiEdit && (upscaleAnalysis || upscaleJobError)"
                  :job="upscaleJob"
                  :regions="upscaleRegionCards"
                  :show-boxes.sync="showUpscaleRegions"
                  :layers-active="upscaleLayersActive"
                  :hovered-id="hoveredUpscaleRegion"
                  :error="upscaleJobError"
                  :preview-error="upscalePreviewError"
                  :region-edit="regionEditState"
                  :edit-running="regionEditBusy"
                  :running-ids="regionEditRunningIds"
                  :more-regions="upscaleMoreRegions"
                  :edits="regionEdits"
                  @opacity="setUpscaleRegionOpacity($event.id, $event.value)"
                  @focus="openUpscaleFace($event.id)"
                  @hover="hoveredUpscaleRegion = $event"
                  @rerender="startRegionEdit($event.region, $event.instruction, $event.inputs || $event.referenceMode)"
                  @edit="openRegionEditLarge($event.id)"
                  @revert="revertRegionEdit($event)"
              />


              <div class="preview-box preview-box--main le-card">
                <div class="text-caption grey--text mb-1">Vorschau Kunde</div>
                <v-btn
                    @click="
                  downloadFileFromUrl(
                    customizationData.artworkImageUrl,
                    `${task.orderName}_artwork`
                  )
                "
                    class="preview-box--download-btn"
                    icon
                >
                  <v-icon>mdi-download</v-icon>
                </v-btn>
                <img
                    @click="() => (dialogImage = customizationData.previewImg)"
                    :src="customizationData.previewImg"
                    class="preview-img preview-img-main"
                    :key="task.updateKey"
                />
              </div>
              <!-- Ersetzt den bisherigen "Original Upload" preview-box-small Block -->

              <div
                  v-if="customizationData.originalImage"
                  class="le-card"
              >
                <div class="d-flex" style="gap: 8px; flex-wrap: wrap;">

                  <!-- Originale — alle Quell-Fotos (Kombination: 2–4) -->
                  <div
                      v-for="(orig, oi) in originalsList"
                      :key="'orig-' + oi"
                      class="preview-box preview-box-small"
                      style="flex:1 1 90px; min-width:90px;"
                  >
                    <div class="text-caption grey--text mb-1" style="font-size:10px;">
                      {{ originalsList.length > 1 ? 'Original ' + (oi + 1) : 'Original' }}
                    </div>
                    <v-btn
                        @click="downloadFileFromUrl(orig, `${task.orderName}_original${oi ? oi + 1 : ''}`)"
                        class="preview-box--download-btn"
                        icon x-small
                    >
                      <v-icon small>mdi-download</v-icon>
                    </v-btn>
                    <img
                        @click="() => (dialogImage = orig)"
                        :src="orig"
                        :key="task.updateKey + '-orig-' + oi"
                        class="preview-img preview-img-small"
                    />
                  </div>

                  <!-- Freepik Upscale ODER Cropped — nur wenn vorhanden -->
                  <div
                      v-if="customizationData.originalUpscaledUrl || customizationData.croppedUrl"
                      class="preview-box preview-box-small"
                      style="flex:1; min-width:0; position:relative;"
                  >
                    <!-- U = Upscaled, C = Cropped -->
                    <v-chip
                        x-small
                        :color="customizationData.originalUpscaledUrl ? 'primary' : 'grey darken-1'"
                        dark
                        style="position:absolute; top:6px; left:6px; z-index:2; font-weight:bold;"
                    >
                      {{ customizationData.originalUpscaledUrl ? 'U' : 'C' }}
                    </v-chip>

                    <div class="text-caption grey--text mb-1" style="font-size:10px;">
                      {{ customizationData.originalUpscaledUrl ? 'Freepik Upscale' : 'Cutout' }}
                    </div>
                    <v-btn
                        @click="downloadFileFromUrl(
            customizationData.originalUpscaledUrl || customizationData.croppedUrl,
            `${task.orderName}_${customizationData.originalUpscaledUrl ? 'upscaled' : 'cropped'}`
          )"
                        class="preview-box--download-btn"
                        icon x-small
                    >
                      <v-icon small>mdi-download</v-icon>
                    </v-btn>
                    <img
                        @click="() => (dialogImage = customizationData.originalUpscaledUrl || customizationData.croppedUrl)"
                        :src="customizationData.originalUpscaledUrl || customizationData.croppedUrl"
                        :key="task.updateKey"
                        class="preview-img preview-img-small"
                    />
                  </div>

                </div>
              </div>

              </div>

              <div v-show="sidebarTab === 'design'">
              <!-- TEXT SETTINGS -->
              <div class="d-flex justify-space-between align-center mb-2">
                <div class="le-section-title"><v-icon small class="mr-1">mdi-format-text</v-icon>Texte</div>
                <div>
                  <v-btn icon small @click="deleteTexts">
                    <v-icon>mdi-delete-forever</v-icon>
                  </v-btn>
                  <v-btn icon small @click="resetTexts">
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- KI-Textvorschlag (Auto-Fix nach 👎 + Kommentar): Umschalter
                   Aktuell / KI-Vorschlag — wird NIE automatisch angewendet -->
              <div v-if="aiConfigProposal" class="ai-proposal-box mb-3">
                <div class="ai-proposal-box__head">
                  <v-icon small color="#7b5cd6">mdi-creation</v-icon>
                  KI-Textvorschlag
                </div>
                <div v-if="aiProposalRequest" class="ai-proposal-box__request">
                  „{{ aiProposalRequest }}“
                </div>
                <div class="ai-proposal-toggle">
                  <button
                      type="button"
                      class="ai-proposal-toggle__btn"
                      :class="{ 'ai-proposal-toggle__btn--active': !aiProposalActive }"
                      @click="applyAiProposal(false)"
                  >
                    Aktuell
                  </button>
                  <button
                      type="button"
                      class="ai-proposal-toggle__btn"
                      :class="{ 'ai-proposal-toggle__btn--active': aiProposalActive }"
                      @click="applyAiProposal(true)"
                  >
                    KI-Vorschlag
                  </button>
                </div>
                <div v-if="aiProposalDiff.length" class="ai-proposal-box__diff">
                  <span v-for="(line, i) in aiProposalDiff" :key="i">{{ line }}</span>
                </div>
              </div>

              <!-- Hintergrund-Layer (transparente Artworks, z. B. Rustic v3):
                   Farbe liegt als Daten in configuration.background und wird
                   erst beim Final-Export eingebrannt — hier nachträglich
                   änderbar. Vorbereitet für spätere Typen (Bild/Muster). -->
              <div v-if="hasBackgroundLayer" class="bg-layer-picker mb-3">
                <div class="field-label">Hintergrund</div>
                <div class="bg-layer-picker__row">
                  <div
                      v-for="opt in backgroundLayerOptions"
                      :key="opt.value"
                      class="bg-layer-picker__swatch"
                      :class="{ 'bg-layer-picker__swatch--selected': localBackground === opt.value }"
                      :style="backgroundSwatchStyle(opt)"
                      :title="opt.label"
                      @click="localBackground = opt.value"
                  />
                  <!-- Freier Farbwähler (Kunden können beliebige Hex-Farben wählen) -->
                  <label
                      class="bg-layer-picker__swatch bg-layer-picker__swatch--custom"
                      :class="{ 'bg-layer-picker__swatch--selected': isCustomBackgroundColor }"
                      :style="isCustomBackgroundColor ? { backgroundColor: localBackground } : {}"
                      title="Eigene Farbe"
                  >
                    <input
                        type="color"
                        class="bg-layer-picker__colorinput"
                        :value="isCustomBackgroundColor ? localBackground : '#ffffff'"
                        @input="localBackground = $event.target.value"
                    />
                  </label>
                </div>
              </div>

              <!-- Emojis/Sonderzeichen in den Texten: mit den Schriften dieses Rechners (wie der Kunde sie
                   im Shop sah) oder mit unseren einheitlichen. Nur sichtbar, wenn ein Text welche enthält. -->
              <div v-if="textsHaveSpecialGlyphs" class="emoji-font-row">
                <span class="emoji-font-row__label" title="Wie Emojis und Sonderzeichen (♡ ★ ♪) in den Texten gesetzt werden">Emojis</span>
                <div class="print-mode">
                  <button type="button" class="print-mode__btn" :class="{ 'print-mode__btn--active': emojiFont === 'system' }" title="Schriften dieses Rechners — Druckdatei wird lokal erzeugt" @click="emojiFont = 'system'">
                    System
                  </button>
                  <button type="button" class="print-mode__btn" :class="{ 'print-mode__btn--active': emojiFont === 'ohmy' }" title="Unsere einheitlichen Emojis (Twemoji) — auch auf dem Server druckbar" @click="emojiFont = 'ohmy'">
                    Unsere
                  </button>
                </div>
              </div>

              <v-expansion-panels accordion multiple v-model="openPanels" flat>
                <v-expansion-panel
                    v-for="(element, index) in localTextElements"
                    :key="index"
                    :ref="'textPanel' + index"
                    class="grey lighten-5 mb-2 rounded border-panel"
                >
                  <v-expansion-panel-header class="py-2">
                    Text #{{ index + 1 }}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="field-label">Text</div>
                    <v-text-field
                        v-model="element.text"
                        :ref="'textInput' + index"
                        solo
                        flat
                        dense
                        hide-details
                        background-color="#f4f5f7"
                        class="mb-3"
                    ></v-text-field>

                    <v-row dense>
                      <v-col cols="8">
                        <div class="field-label">Schriftart</div>
                        <v-select
                            v-model="element.font"
                            :items="fontOptions"
                            solo
                            flat
                            dense
                            hide-details
                            background-color="#f4f5f7"
                            :menu-props="{ bottom: true, offsetY: true }"
                        >
                          <template v-slot:item="{ item }">
                            <span :style="{ fontFamily: item }">{{ item }}</span>
                          </template>
                        </v-select>
                      </v-col>
                      <v-col cols="4">
                        <div class="field-label">Farbe</div>
                        <div
                            class="color-swatch"
                            :style="{ backgroundColor: element.color }"
                            @click="openColorPicker(index)"
                        >
                          <input
                              type="color"
                              v-model="element.color"
                              :ref="'cp' + index"
                              class="hidden-input"
                          />
                        </div>
                      </v-col>
                    </v-row>

                    <div class="mt-4">
                      <div class="caption grey--text">
                        Size ({{ element.size }}%)
                      </div>
                      <v-slider
                          v-model="element.size"
                          min="1"
                          max="20"
                          step="0.1"
                          dense
                          hide-details
                      ></v-slider>
                    </div>

                    <div class="mt-2">
                      <div class="caption grey--text">
                        Pos Y ({{ element.y }}%)
                      </div>
                      <v-slider
                          v-model="element.y"
                          min="0"
                          max="100"
                          step="0.5"
                          dense
                          hide-details
                      ></v-slider>
                    </div>

                    <!-- Lesbarkeit verbessern -->
                    <div class="mt-3 d-flex align-center justify-space-between">
                      <div class="caption grey--text">Lesbarkeit verbessern</div>
                      <v-switch
                          :input-value="element.bgEnabled"
                          @change="val => onToggleBg(index, val)"
                          dense
                          hide-details
                          class="mt-0 pt-0"
                      ></v-switch>
                    </div>

                    <div v-if="element.bgEnabled" class="mt-2">
                      <div class="caption grey--text">
                        Intensität ({{ ((element.bgIntensity != null ? element.bgIntensity : 0.45) * 100).toFixed(0) }}%)
                      </div>
                      <v-slider
                          v-model="element.bgIntensity"
                          min="0.1"
                          max="0.9"
                          step="0.01"
                          dense
                          hide-details
                      ></v-slider>

                      <!-- Wolkenfarbe: Auto (hell/dunkel je Textfarbe) oder frei wählbar -->
                      <div class="d-flex align-center justify-space-between mt-2">
                        <div class="caption grey--text">Wolkenfarbe</div>
                        <div class="d-flex align-center" style="gap: 6px">
                          <button
                              type="button"
                              class="cloud-auto-chip"
                              :class="{ 'cloud-auto-chip--active': !element.bgColor }"
                              @click="setCloudColor(index, null)"
                          >
                            Auto
                          </button>
                          <div
                              class="color-swatch color-swatch--small"
                              :style="{ backgroundColor: cloudColor(element) }"
                              @click="openCloudColorPicker(index)"
                          >
                            <input
                                type="color"
                                :value="cloudColor(element)"
                                :ref="'cloudcp' + index"
                                class="hidden-input"
                                @input="e => setCloudColor(index, e.target.value)"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>

              <v-divider class="mt-4 mb-3"></v-divider>
              <!-- ARTWORK SETTINGS -->
              <div class="d-flex justify-space-between align-center mb-2">
                <div class="le-section-title"><v-icon small class="mr-1">mdi-image-outline</v-icon>Artwork</div>
                <v-btn icon small @click="resetArtworkTransform">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </div>

              <v-expansion-panels
                  accordion
                  multiple
                  flat
                  v-model="openArtworkPanels"
              >
                <v-expansion-panel
                    class="grey lighten-5 mb-2 rounded border-panel"
                >
                  <v-expansion-panel-header class="py-2">
                    Drehung / Format
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <!-- Rotation -->
                    <div class="mb-4">
                      <div class="caption grey--text mb-1">
                        Rotation ({{ (localCanvas.rotation || 0).toFixed(0) }}°)
                      </div>
                      <div class="d-flex align-center">
                        <v-btn icon small @click="rotateLeft90">
                          <v-icon small>mdi-rotate-left</v-icon>
                        </v-btn>
                        <v-btn
                            icon
                            small
                            @click="localCanvas.rotation = localCanvas.rotation - 1"
                        >
                          <v-icon small>mdi-minus</v-icon>
                        </v-btn>
                        <v-slider
                            v-model="localCanvas.rotation"
                            :min="-180"
                            :max="180"
                            step="1"
                            dense
                            hide-details
                            class="mx-2"
                        ></v-slider>
                        <v-btn
                            icon
                            small
                            @click="localCanvas.rotation = localCanvas.rotation + 1"
                        >
                          <v-icon small>mdi-plus</v-icon>
                        </v-btn>
                        <v-btn icon small @click="rotateRight90">
                          <v-icon small>mdi-rotate-right</v-icon>
                        </v-btn>
                      </div>
                    </div>

                    <!-- Canvas Format -->
                    <div class="mb-4">
                      <div class="caption grey--text mb-1">
                        Canvas-Format
                      </div>
                      <v-btn-toggle
                          v-model="localOrientation"
                          dense
                          rounded
                          mandatory
                          style="display: flex"
                          @change="onOrientationChange"
                      >
                        <v-btn style="flex: 1" value="portrait">Hochformat</v-btn>
                        <v-btn style="flex: 1" value="landscape">Querformat</v-btn>
                      </v-btn-toggle>

                      <!-- KI-Formatwechsel: Illustration selbst umbauen (nicht nur Canvas) -->
                      <v-btn
                          v-if="canAiEdit"
                          small
                          block
                          depressed
                          color="primary"
                          class="mt-2"
                          :loading="aiEditBusy"
                          :disabled="aiEditPending || !currentImageUrl"
                          @click="runAiReformat"
                      >
                        <v-icon left small>mdi-aspect-ratio</v-icon>
                        Mit KI ins {{ localOrientation === "portrait" ? "Querformat" : "Hochformat" }}
                      </v-btn>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel class="grey lighten-5 mb-2 rounded border-panel">
                  <v-expansion-panel-header class="py-2">
                    Passepartout
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <!-- An/Aus -->
                    <div class="d-flex align-center justify-space-between mb-2">
                      <div class="caption grey--text">Passepartout</div>
                      <v-switch
                          :input-value="localCanvas.mat > 0"
                          @change="val => toggleMat(val)"
                          dense
                          hide-details
                          class="mt-0 pt-0"
                      ></v-switch>
                    </div>

                    <!-- Breite -->
                    <div v-if="localCanvas.mat > 0">
                      <div class="caption grey--text mb-1">
                        Breite ({{ (localCanvas.mat || 0).toFixed(0) }}%)
                      </div>
                      <v-slider
                          v-model="localCanvas.mat"
                          :min="matMin"
                          :max="matMax"
                          step="0.5"
                          dense
                          hide-details
                          @input="onMatChange"
                      ></v-slider>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
              </div>
            </div>

            <!-- SEND TO FULFILL (immer sichtbar, sticky unten) -->

            <div class="live-action-container">
              <template v-if="!isRatingView && !isRefinementView">
                <div class="d-flex">
                  <button
                      type="button"
                      class="le-flag-btn mt-2 mr-2"
                      title="Flaggen"
                      @click="() => openRevisionDialog(true)"
                  >
                    <v-icon small color="#fff">mdi-flag</v-icon>
                    <span class="le-flag-btn__label">Flaggen</span>
                  </button>

                  <div class="flex-grow-1 d-flex flex-column">
                    <v-btn
                        block
                        class="mt-2"
                        color="warning"
                        :loading="isSending"
                        :disabled="isSending || !currentImageUrl"
                        @click="openRevisionDialog(false)"
                    >
                      <v-icon left>mdi-shaker-outline</v-icon>
                      Überarbeitung Nötig
                    </v-btn>
                  </div>
                </div>
                <v-divider class="mt-2 mb-2"></v-divider>
              </template>
              <div v-if="!isRefinementView" class="d-flex align-center">
                <!-- Fulfill: links, nur Icon — Label bei Hover; Klick 1 = "Sicher?",
                     Klick 2 (innerhalb 4s) = wirklich fulfillen -->
                <button
                    v-if="!isRatingView"
                    type="button"
                    class="le-fulfill-btn mr-2"
                    :class="{ 'le-fulfill-btn--armed': fulfillArmed }"
                    :disabled="isSending || !currentImageUrl"
                    :title="fulfillArmed ? 'Zum Bestätigen erneut klicken' : 'Fulfill'"
                    @click="onFulfillClick"
                >
                  <v-progress-circular
                      v-if="isSending && fulfillWasConfirmed"
                      indeterminate
                      size="18"
                      width="2"
                      color="#fff"
                  />
                  <v-icon v-else small color="#fff">
                    {{ fulfillArmed ? "mdi-alert-circle-outline" : "mdi-truck-check" }}
                  </v-icon>
                  <span class="le-fulfill-btn__label">
                    {{ fulfillArmed ? "Sicher? Erneut klicken" : "Fulfill" }}
                  </span>
                </button>
                <v-btn
                    class="flex-grow-1"
                    color="primary"
                    :loading="isSending && !fulfillWasConfirmed"
                    :disabled="isSending || !currentImageUrl"
                    @click="() => sendToFulfill(true)"
                >
                  <v-icon left>mdi-truck-check</v-icon>
                  <v-icon left>mdi-mail</v-icon>
                  Preview
                </v-btn>
              </div>
              <template v-if="isRefinementView">
                <v-btn
                    v-if="!isRatingView"
                    block
                    color="success"
                    class="mt-2"
                    :loading="isSending"
                    :disabled="isSending || !localRefinementArtwork"
                    @click="() => approveRefinement()"
                >
                  <v-icon left>mdi-check</v-icon>
                  Upload
                </v-btn>
              </template>
              <div
                  v-if="isRefinementView && !localRefinementArtwork"
                  class="mt-2 red--text text-caption"
              >
                Please Upload Refinement
              </div>
              <div v-if="sendError" class="mt-2 red--text text-caption">
                {{ sendError }}
              </div>
              <!-- Druckdatei: Server (Standard) oder lokal im Browser. Scheitert der Server, springt der
                   Schalter für diesen Task automatisch auf „Lokal" und der Import läuft im Browser. -->
              <div v-if="!isRefinementView" class="print-mode-row">
                <div class="print-mode" title="Wo die Druckdatei für Preview und Fulfill erzeugt wird">
                  <button type="button" class="print-mode__btn" :class="{ 'print-mode__btn--active': printOnServer }" :disabled="isSending" @click="setPrintMode(true)">
                    <v-icon x-small>mdi-cloud-outline</v-icon>
                    Server
                  </button>
                  <button type="button" class="print-mode__btn" :class="{ 'print-mode__btn--active': !printOnServer }" :disabled="isSending" @click="setPrintMode(false)">
                    <v-icon x-small>mdi-laptop</v-icon>
                    Lokal
                  </button>
                </div>
                <div class="print-mode-row__right">
                  <button v-if="isAdminUser" type="button" class="print-mode__compare print-mode__compare--icon" :disabled="printCompare.busy || !currentImageUrl" title="Vorschau: Druckdatei aus Browser und Server rendern und vergleichen — verändert den Task nicht" @click="runPrintCompare">
                    <v-progress-circular v-if="printCompare.busy" indeterminate size="11" width="2" />
                    <v-icon v-else small>mdi-compare</v-icon>
                  </button>
                  <!-- Nach Fulfill/Import automatisch den nächsten offenen Task öffnen (Geräte-Einstellung) -->
                  <button
                      type="button"
                      class="print-mode__next"
                      :class="{ 'print-mode__next--on': autoNext }"
                      :title="autoNext ? 'Danach nächsten Task öffnen: an' : 'Danach nächsten Task öffnen: aus'"
                      @click="autoNext = !autoNext; persistAutoNext(autoNext)"
                  >
                    <v-icon x-small>mdi-fast-forward-outline</v-icon>
                    Nächster
                    <span class="print-mode__dot"></span>
                  </button>
                </div>
              </div>
              <div v-if="emojiLocalReason && serverPrintFile && !serverPrintFallback && !isRefinementView" class="print-mode__note print-mode__note--info">
                {{ emojiLocalReason }}
              </div>
              <div v-if="serverPrintFallback && !isRefinementView" class="print-mode__note">
                Server-Rendering fehlgeschlagen, dieser Task wird lokal importiert: {{ serverPrintFallback }}
              </div>
              <div v-if="printCompare.error" class="print-mode__note">{{ printCompare.error }}</div>
            </div>
          </div>
        </div>
      </div>
    </v-card>

    <!-- Bild-Vorschau -->
    <ImageDialog
        v-if="dialogImage"
        :show="!!dialogImage"
        :image="dialogImage"
        :onClose="() => (dialogImage = null)"
    ></ImageDialog>

    <!-- Vergleichs-Dialog -->
    <v-dialog v-model="compareDialog" width="96vw" max-width="96vw" content-class="compare-dialog">
      <v-card class="compare-dialog-card">
        <!-- Kopfzeile = Reiter (Gesamtbild · alle Gesichter · je Gesicht/Region · Druckdatei) + Zurücksetzen/Schließen -->
        <div class="compare-tabs">
          <template v-if="canAiEdit && (compareFaceRegions.length || printCompare.serverUrl)">
          <span class="compare-tab" :class="{ 'compare-tab--active': compareTab === 'overview' }" @click="compareTab = 'overview'">
            <v-icon x-small>mdi-image-outline</v-icon> Gesamtbild
          </span>
          <span
              v-if="compareFaceRegions.length > 1"
              class="compare-tab"
              :class="{ 'compare-tab--active': compareTab === 'faces' }"
              @click="compareTab = 'faces'"
          >
            <v-icon x-small>mdi-face-recognition</v-icon> {{ compareFacesLabel }}
          </span>
          <span
              v-for="region in compareFaceRegions"
              :key="'cmp-tab-' + region.id"
              class="compare-tab"
              :class="{ 'compare-tab--active': compareTab === region.id || (compareTab === 'faces' && compareFaceRegions.length === 1) }"
              @click="compareTab = region.id"
          >
            <span class="compare-tab__dot" :style="{ background: upscaleScoreColor(region.currentScore) }"></span>{{ region.label }}
          </span>
          <span
              v-if="printCompare.serverUrl"
              class="compare-tab"
              :class="{ 'compare-tab--active': compareTab === 'print' }"
              @click="compareTab = 'print'"
          >
            <v-icon x-small>mdi-printer-outline</v-icon> Druckdatei
          </span>
          </template>
          <span v-else class="compare-tabs__title">Vergleich</span>
          <span class="compare-tabs__spacer"></span>
          <span
              v-if="compareTab !== 'overview' && compareTab !== 'print'"
              class="compare-tab compare-tab--ghost"
              :title="compareFaceTight ? 'Ganzen Ausschnitt mit Umgebung zeigen' : 'Eng auf das Gesicht zoomen'"
              @click="compareFaceTight = !compareFaceTight"
          >
            <v-icon x-small>{{ compareFaceTight ? "mdi-arrow-expand-all" : "mdi-arrow-collapse-all" }}</v-icon>
            {{ compareFaceTight ? "Mit Umgebung" : "Nur Gesicht" }}
          </span>
          <v-btn v-if="compareTab === 'overview' || compareTab === 'print'" icon small title="Zoom zurücksetzen" @click="resetCompareView">
            <v-icon small>mdi-refresh</v-icon>
          </v-btn>
          <v-btn icon small title="Schließen" @click="compareDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
        <!-- Druckdatei: im Browser erzeugt | auf dem Server erzeugt (gleicher Zoom, gleiches Verschieben) -->
        <v-card-text v-if="compareTab === 'print'" class="compare-print">
          <div
              class="compare-dialog-container"
              @mousedown.prevent="compareOnMouseDown"
              @mousemove.prevent="compareOnMouseMove"
              @mouseup.prevent="compareOnMouseUp"
              @mouseleave.prevent="compareOnMouseUp"
              @wheel.prevent="compareOnWheel"
          >
            <div class="compare-pane">
              <div class="compare-pane-label">Druckdatei aus dem Browser (heutiger Weg)</div>
              <div class="compare-pane-inner">
                <img :src="printCompare.browserUrl" class="compare-dialog-img" draggable="false" :style="compareTransformStyle" />
              </div>
            </div>
            <div class="compare-pane">
              <div class="compare-pane-label">Druckdatei vom Server</div>
              <div class="compare-pane-inner">
                <img :src="printCompare.serverUrl" class="compare-dialog-img" draggable="false" :style="compareTransformStyle" />
              </div>
            </div>
          </div>
          <div class="caption grey--text text-center mt-2">
            Mittlere Abweichung {{ printCompare.meanDiff }} von 255 · deutlich abweichende Pixel {{ printCompare.changedPct }} % · Server {{ printCompare.ms }} ms
            <template v-if="printCompare.emojiTexts.length"> · <b class="red--text">Zeichen ohne Server-Schrift: {{ printCompare.emojiTexts.join(" · ") }} — dieser Task wird lokal importiert</b></template>
            · Scrollrad = Zoom · Mausziehen = Verschieben
          </div>
        </v-card-text>
        <!-- Gesichtsvergleich: je Region Foto | Upscale | Überarbeitung, mit Deckkraft-Regler -->
        <v-card-text v-else-if="compareTab !== 'overview'" class="compare-regions">
          <div class="compare-faces" :class="{ 'compare-faces--single': compareShownRegions.length === 1 }">
            <UpscaleRegionCard
                v-for="region in compareShownRegions"
                :key="'cmp-face-' + region.id"
                :region="region"
                :limit="upscaleScoreLimit"
                :enabled="upscaleLayersActive"
                :tight="compareFaceTight"
                large
                :wheel-zoom="compareShownRegions.length === 1"
                :editable="canAiEdit"
                :edit-busy="regionEditBusy || isRegionEditRunning(region.id)"
                :edit-running="isRegionEditRunning(region.id)"
                :edit-initially-open="regionEditFocusId === region.id"
                @opacity="setUpscaleRegionOpacity($event.id, $event.value)"
                @rerender="startRegionEdit($event.region, $event.instruction, $event.inputs || $event.referenceMode)"
                @version="activateRegionVersion($event.id, $event.index)"
            />
          </div>
        </v-card-text>
        <v-card-text v-else class="compare-overview">
          <!-- Links = Master: Zoom/Verschieben nimmt die Originale mit. Im Original selbst wird nur
               dessen Abweichung zum Master geändert (bleibt beim nächsten Master-Zug erhalten). -->
          <div
              class="compare-dialog-container"
              @mousemove.prevent="compareOnMouseMove"
              @mouseup.prevent="compareOnMouseUp"
              @mouseleave.prevent="compareOnMouseUp"
          >
            <div class="compare-pane" @mousedown.prevent="compareOnMouseDown($event, 'main')" @wheel.prevent="compareOnWheel($event, 'main')">
              <div class="compare-pane-label">{{ compareMainLabel }}</div>
              <div class="compare-pane-inner" ref="comparePaneArtwork">
                <img
                    v-if="currentImageUrl"
                    :src="artworkDisplayUrl || currentImageUrl"
                    class="compare-dialog-img"
                    draggable="false"
                    :style="compareTransformStyle"
                />
                <!-- Versionen wie in der Versionsleiste, direkt im Bild-Rahmen -->
                <div
                    v-if="compareVersionItems.length > 1"
                    ref="compareVersionPill"
                    class="compare-orig-switch compare-orig-switch--scroll"
                    @mousedown.stop
                    @mousemove.stop
                    @mouseup.stop
                    @wheel.stop
                >
                  <template v-for="(item, i) in compareVersionItems">
                    <v-icon v-if="item.arrow" :key="'cmp-varr-' + i" x-small class="compare-orig-switch__arrow">mdi-chevron-right</v-icon>
                    <button
                        v-else
                        :key="'cmp-ver-' + item.key"
                        type="button"
                        :class="{ active: selectedVariant === item.key, disabled: item.disabled }"
                        :title="item.title || ''"
                        :disabled="item.disabled"
                        @click="!item.disabled && (selectedVariant = item.key)"
                    >{{ item.label }}<span v-if="item.previewIcon" class="le-version-mail" :title="previewSentTitle"><v-icon>mdi-email-outline</v-icon></span></button>
                  </template>
                </div>
                <v-tooltip v-if="compareCustomerArtwork" top>
                  <template #activator="{ on }">
                    <button
                        type="button"
                        class="compare-pane-toggle"
                        :class="{ active: compareShowArtwork }"
                        v-on="on"
                        @mousedown.stop
                        @mouseup.stop
                        @wheel.stop
                        @click="compareShowArtwork = !compareShowArtwork"
                    >
                      <v-icon small>{{ compareShowArtwork ? "mdi-eye-outline" : "mdi-eye-off-outline" }}</v-icon>
                    </button>
                  </template>
                  <span>Artwork (Kundenansicht) {{ compareShowArtwork ? "ausblenden" : "einblenden" }}</span>
                </v-tooltip>
              </div>
            </div>
            <div
                v-for="item in compareShownOriginals"
                :key="'compare-orig-' + item.index"
                class="compare-pane"
                @mousedown.prevent="compareOnMouseDown($event, 'orig')"
                @wheel.prevent="compareOnWheel($event, 'orig')"
            >
              <div class="compare-pane-label">
                {{ compareOriginals.length > 1 ? "Original " + (item.index + 1) : "Original" }}
              </div>
              <div class="compare-pane-inner">
                <img
                    :src="item.url"
                    class="compare-dialog-img"
                    draggable="false"
                    :style="compareOrigTransformStyle"
                />
                <!-- Umschalter 1 / 2 / Alle direkt im Foto-Rahmen (nur im ersten gezeigten) -->
                <div
                    v-if="compareOriginals.length > 1 && item.index === compareShownOriginals[0].index"
                    class="compare-orig-switch"
                    @mousedown.stop
                    @mousemove.stop
                    @mouseup.stop
                    @wheel.stop
                >
                  <button
                      v-for="(original, index) in compareOriginals"
                      :key="'cmp-orig-pick-' + index"
                      type="button"
                      :class="{ active: compareOriginalIndex === index }"
                      @click="compareOriginalIndex = index"
                  >{{ index + 1 }}</button>
                  <button type="button" :class="{ active: compareOriginalIndex === 'all' }" @click="compareOriginalIndex = 'all'">Alle</button>
                </div>
              </div>
            </div>
            <!-- Das Artwork, wie der Kunde es im Editor gesehen und bestellt hat (vor dem Upscale) -->
            <div v-if="compareShowArtwork && compareCustomerArtwork" class="compare-pane" @mousedown.prevent="compareOnMouseDown($event, 'main')" @wheel.prevent="compareOnWheel($event, 'main')">
              <div class="compare-pane-label">Artwork (Kundenansicht)</div>
              <div class="compare-pane-inner">
                <img
                    :src="compareCustomerArtwork"
                    class="compare-dialog-img"
                    draggable="false"
                    :style="compareTransformStyle"
                />
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Gewählter KI-Edit ist nicht freigegeben: vor der Überarbeitung fragen -->
    <v-dialog v-model="showReleaseOnRefinementDialog" max-width="470" persistent>
      <v-card>
        <v-card-title>KI-Edit freigeben?</v-card-title>
        <v-card-text>
          Die gewählte Variante ist ein <strong>KI-Edit</strong>, den nur Nutzer
          mit AI_EDIT-Berechtigung sehen. Ohne Freigabe sieht die Person in der
          Überarbeitung dieses Bild nicht.
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="showReleaseOnRefinementDialog = false">Abbrechen</v-btn>
          <v-spacer />
          <v-btn color="primary" depressed @click="releaseAndFinishRevision">
            <v-icon small class="mr-1">mdi-eye-check-outline</v-icon>
            Freigeben &amp; weiter
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Überarbeitung-Dialog -->
    <v-dialog v-model="showRevisionDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="headline"> Überarbeitung nötig </v-card-title>
        <v-card-text>
          <div class="floating-actions">
            <v-btn
                @click="
                revisionComment =
                  'Remove watercolor stains from Faces/Skin'
              "
                x-small
                outlined
                class="ma-1"
            >
              Remove stains In Face
            </v-btn>
            <v-btn
                @click="revisionComment = 'Optimize Face Details'"
                x-small
                outlined
                class="ma-1"
            >
              Optimize Face Details
            </v-btn>
            <v-btn
                @click="revisionComment = 'Remove Numberplate'"
                x-small
                outlined
                class="ma-1"
            >
              Remove Numberplate
            </v-btn>
          </div>
          <div class="text-field-wrapper">
            <v-textarea
                v-model="revisionComment"
                outlined
                label="Beschreibung"
                rows="4"
            ></v-textarea>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showRevisionDialog = false"> Abbrechen </v-btn>
          <v-btn color="primary" @click="confirmRevision"> Bestätigen </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import ImageDialog from "@/components/ImageDialog.vue";
import FeedbackThread from "@/components/AllRatings/FeedbackThread.vue";
import {
  countryFlagEmoji,
  currentMonthYear,
  downloadFileFromUrl,
  downloadImageFromUrl,
  getDateFromNow,
} from "@/utils";
import { mapActions, mapGetters } from "vuex";
import { doc, setDoc, arrayUnion } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

/** Bild-Vorlade-Cache: hält bis zu 150 Image-Objekte referenziert, damit der Browser sie nicht wieder verwirft */
const PRELOAD_CACHE = new Map();
function preloadImages(urls) {
  for (const url of urls) {
    if (!url || typeof url !== "string" || !url.startsWith("http") || PRELOAD_CACHE.has(url)) continue;
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    PRELOAD_CACHE.set(url, img);
    if (PRELOAD_CACHE.size > 150) PRELOAD_CACHE.delete(PRELOAD_CACHE.keys().next().value);
  }
}
import { db, functions as fbFunctions } from "../../../fb";
import { EventBus, events } from "../../../event-bus";
import { permissions } from "@/constants";
import upscaleLayersMixin from "@/components/Upscales/upscaleLayersMixin";
import serverPrintMixin from "@/components/Upscales/serverPrintMixin";
import regionEditMixin, { REGION_TYPES } from "@/components/Upscales/regionEditMixin";
import { DEFAULT_EMOJI_FONT, hasSpecialGlyphs, textFontStack } from "@/emojiFont";
import { scoreColor } from "@/upscaleJobs";
import UpscaleRegionsPanel from "@/components/Upscales/UpscaleRegionsPanel.vue";
import UpscaleRegionCard from "@/components/Upscales/UpscaleRegionCard.vue";

// Default-Intensität der Lesbarkeits-Wolke (0..1)
const DEFAULT_BG_INTENSITY = 0.45;

// Textur-Hintergründe (Katalog identisch zum Kunden-Editor ohmy-ai, dort in
// lib/styles.js gepflegt — Reihenfolge: 4 Favoriten zuerst). Assets liegen im
// ohmyart-ai-Bucket: thumbs 400×400 für die Anzeige, full 4000×6000/6000×4000
// (2:3 bzw. 3:2, gemischt) für den Final-Export.
const BACKGROUND_IMAGE_IDS = [
  "bg00021", "bg00022", "bg00002", "bg00003", "bg00009", "bg00014", "bg00010",
  "bg00013", "bg00001", "bg00020", "bg00000", "bg00004", "bg00005",
  "bg00006", "bg00007", "bg00008", "bg00011", "bg00012", "bg00015",
  "bg00016", "bg00018", "bg00019",
];

// preview liegt als WebP (deutlich kleiner bei Texturen), thumbs/full als JPG
const backgroundAssetUrl = (id, size = "thumbs") => {
  const ext = size === "preview" ? "webp" : "jpg";
  return `https://firebasestorage.googleapis.com/v0/b/ohmyart-ai.firebasestorage.app/o/app%2Fbackgrounds%2F${size}%2F${encodeURIComponent(id)}.${ext}?alt=media`;
};

// Die Textur folgt IMMER der Artwork-Ausrichtung (Hochformat → Maserung
// vertikal). Nur bg00013 liegt nativ hochkant vor — weicht die native
// Ausrichtung vom Ziel ab, wird die (quadratische) Thumb-Vorschau 90° gedreht;
// der Final-Export dreht das Full-Size-Asset anhand seiner echten Maße.
const BACKGROUND_PORTRAIT_NATIVE = new Set(["bg00013", "bg00021", "bg00022"]);

// „Original" (bg00021) hat eine Doppelrolle: gewählt → das echte opake
// Original-Artwork wird gezeigt/exportiert (kein Freisteller, kein Layer)
const ORIGINAL_BACKGROUND_ID = "bg00021";
const isBackgroundTextureRotated = (id, orientation) =>
  BACKGROUND_PORTRAIT_NATIVE.has(id) !== (orientation === "portrait");

// Anzeigenamen (deutsch — mehrsprachige Pendants pflegt ohmy-ai in den Locales)
const BACKGROUND_NAMES = {
  bg00021: "Original",
  bg00022: "Warmes Papier",
  bg00000: "Putz Weiß", bg00001: "Beton Anthrazit", bg00002: "Pastellblau",
  bg00003: "Beton Hell", bg00004: "Patina", bg00005: "Holz Dunkel",
  bg00006: "Schiefer", bg00007: "Rosenquarz", bg00008: "Terrakotta",
  bg00009: "Eisblau", bg00010: "Nachtblau", bg00011: "Ozean",
  bg00012: "Rubinrot", bg00013: "Kreideweiß", bg00014: "Holz Vintage",
  bg00015: "Marmor Weiß", bg00016: "Marmor Blau", bg00018: "Eiche",
  bg00019: "Marmor Rosé", bg00020: "Holz Weiß",
};

export default {
  name: "LiveEditorDialog",
  // Upscale-Ebenen (Final über Upscale), Regionen-Rahmen und softes Löschen — siehe Mixin
  mixins: [upscaleLayersMixin, serverPrintMixin, regionEditMixin],
  components: { ImageDialog, FeedbackThread, UpscaleRegionsPanel, UpscaleRegionCard },
  props: {
    value: Boolean,
    task: Object,
    isRatingView: Boolean,
    isRefinementView: Boolean,
    /** Import-Karte: im Server-Modus übernimmt der Server den ganzen Import (Event „serverImport") */
    serverImport: Boolean,
    customizationData: {
      type: Object,
      required: true,
    },
    orderName: String,
    upscaledUrls: {
      type: Object,
      default: () => ({}),
    },
    refinementArtwork: {
      type: String,
      default: null,
    },
    onApprove: {
      type: Function,
      default: null,
    },
    storageUploadFn: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      getDateFromNow,
      isAdminRevision: false,
      showRevisionDialog: false,
      dialogImage: null,

      uid: Math.random().toString(36).slice(2, 9),

      // lokales Custom Artwork (z.B. via Drag & Drop)
      localRefinementArtwork: null,
      isFrameDragOver: false,

      // Kommentar für Überarbeitung
      revisionComment: "",

      // Sidebar-Tab (Übersicht | Text & Artwork)
      sidebarTab: "overview",

      // Auto-ReOpen: nach Abschluss/Edit-Start direkt den nächsten Task öffnen
      autoNext: localStorage.getItem("ohmyLiveEditorAutoNext") !== "false",

      // Fulfill-Bestätigung (Zwei-Klick-Schutz)
      fulfillArmed: false,
      fulfillArmTimer: null,
      fulfillWasConfirmed: false,

      // KI-Änderung (gpt-image-2 + Ultra-Upscale)
      aiEditPrompt: "",
      aiEditBusy: false,
      // KI-Änderung-Box: Einordnung des freien Wunsches (Text-Overlay vs. Bild) läuft
      smartClassifying: false,
      // Lokaler KI-Textvorschlag aus der Box (gleiches Format wie feedback.autoEdit.configProposal)
      localConfigProposal: null,
      releaseMenu: { show: false, x: 0, y: 0, index: null },
      showReleaseOnRefinementDialog: false,
      aiEditError: "",
      localAiEdits: [],
      // KI-Textvorschlag (Auto-Fix): Umschalt-Zustand + Backup des Ist-Stands
      aiProposalActive: false,
      aiProposalBackup: null,
      orderNoteCopied: false,

      // Artwork Canvas State
      localCanvas: { x: 0, y: 0, scale: 1, mat: 0, rotation: 0 },
      localTextElements: [],
      localBackground: null, // Hintergrund-Layer (nur transparente Artworks)

      // Gemessene Textgrößen für die SVG-Wolke: { [idx]: { w, h } }
      textBoxes: {},

      selectedVariant: "ORIGINAL",
      openPanels: [],
      openArtworkPanels: [],

      // Canvas-Ausrichtung
      localOrientation: null,

      isKeyboardZoom: false,

      // Vergleichs-Dialog
      compareDialog: false,
      feedbackShowOriginal: false,
      compareOriginalIndex: localStorage.getItem("ohmyCompareOriginalAll") === "true" ? "all" : 0,
      compareShowArtwork: localStorage.getItem("ohmyCompareShowArtwork") !== "false",
      compareZoom: 2,
      compareOffset: { x: 0, y: 0 },
      // Abweichung der Originale vom Master (Zoomfaktor + Verschiebung), bleibt über Master-Züge erhalten
      compareOrigAdjust: { z: 1, x: 0, y: 0 },
      compareDrag: {
        active: false,
        startX: 0,
        startY: 0,
        origX: 0,
        origY: 0,
      },

      matMin: 2,
      matMax: 15,

      // Größen
      canvasRect: { width: 0, height: 0 },
      frameRect: { width: 0, height: 0 },
      imageAspectRatio: 1,

      // Dragging
      dragMode: null,
      dragStart: { x: 0, y: 0 },
      dragStartCanvas: { x: 0, y: 0 },
      dragTextIndex: null,
      dragStartTextPos: { x: 0, y: 0 },
      textWasDragged: false,

      zoomProxy: 1,

      isSending: false,
      sendError: null,
      regionTypes: REGION_TYPES,
      /** Emojis/Sonderzeichen der Texte: "system" (Schriften dieses Rechners) | "ohmy" (unsere) — siehe src/emojiFont.js */
      emojiFont: DEFAULT_EMOJI_FONT,

      fontOptions: [
        "WindSong",
        "Playfair Display",
        "Montserrat",
        "Raleway",
        "Dancing Script",
        "Anton",
        "Cinzel",
        "Permanent Marker",
      ],
    };
  },
  computed: {
    ...mapGetters(["currentUser", "userPermissions"]),
    /** KI-Funktionen (Prompt-Box, Formatwechsel) nur mit Berechtigung —
     *  bereits erzeugte Edits sieht jeder */
    /** KI-Features (Prüfungs-Panel, KI-Änderung-Box, Region-Edits, Gesichter-Vergleich) nur mit AI_EDIT oder ADMIN */
    canAiEdit() {
      const perms = this.userPermissions || [];
      return perms.includes(permissions.ADMIN) || perms.includes(permissions.AI_EDIT);
    },
    isAdminUser() {
      return (this.userPermissions || []).includes(permissions.ADMIN);
    },
    matPx() {
      const base = Math.min(this.frameRect.width || 0, this.frameRect.height || 0);
      return (base * (this.localCanvas.mat || 0)) / 100;
    },
    // Alle Quell-Fotos: Kombination (2–4) aus originalImages, sonst die
    // klassischen Einzel-Felder (Alt-Tasks kennen nur originalImage/2)
    originalsList() {
      const cd = this.customizationData || {};
      if (Array.isArray(cd.originalImages) && cd.originalImages.length >= 2) {
        return cd.originalImages;
      }
      return [cd.originalImage, cd.originalImage2].filter(Boolean);
    },
    // Transparentes Artwork (rustic v3 & Co): Hintergrund als Daten-Layer
    hasBackgroundLayer() {
      return !!(this.customizationData?.versionMeta?.transparent || this.config?.background);
    },
    backgroundLayerOptions() {
      // Reihenfolge wie im Kunden-Editor: 4 Favoriten-Texturen, Unifarben,
      // dann der restliche Katalog (Zeile bricht um — internes Tool, alles sichtbar)
      const textures = BACKGROUND_IMAGE_IDS.map((id) => ({
        value: id,
        label: BACKGROUND_NAMES[id] || `Textur ${id.replace(/^bg0*/, "")}`,
      }));
      return [
        ...textures.slice(0, 4),
        // Standard-Farben des Farb-Tabs im Kunden-Editor
        { value: "#A9CBB4", label: "Salbei" },
        { value: "#A9C3D3", label: "Taubenblau" },
        { value: "#D98F82", label: "Terrakotta-Rosé" },
        { value: "#E3C77A", label: "Senf" },
        { value: "#F4CFA5", label: "Aprikose" },
        { value: "#B7B9B5", label: "Steingrau" },
        { value: "#ffffff", label: "Weiß" },
        { value: "#9c9c9c", label: "Grau" },
        { value: "#1a1a1a", label: "Schwarz" },
        ...textures.slice(4),
      ];
    },
    // Eigene Farbe (nicht in den Standard-Swatches)?
    isCustomBackgroundColor() {
      const bg = this.localBackground || "";
      if (!bg.startsWith("#")) return false;
      return !this.backgroundLayerOptions.some((o) => o.value === bg);
    },
    // Textur-Hintergrund: URL + Positionierung des gedrehten Vorschau-Quadrats
    // (Unifarben laufen weiter über backgroundColor im artworkClipStyle)
    // „Original" = Original-Kachel ODER native Key-Farbe des Stils
    // (vector generiert auf #EADCCD — diese Farbe gewählt = Original)
    wantsOriginalBg() {
      const vm = this.customizationData?.versionMeta;
      // opaqueOriginal === false (Greenscreen-Stile): Original ist grün/
      // magenta und wird NIE angezeigt/exportiert
      if (vm?.opaqueOriginal === false) return false;
      return (
        this.localBackground === ORIGINAL_BACKGROUND_ID ||
        (!!vm?.nativeBackground && this.localBackground === vm.nativeBackground)
      );
    },
    // „Original" gewählt UND echtes Original verfügbar → Artwork ist opak,
    // Freisteller + Textur-Layer werden nicht gebraucht
    showsOpaqueOriginal() {
      if (!this.hasBackgroundLayer) return false;
      if (!this.wantsOriginalBg) return false;
      const vm = this.customizationData?.versionMeta;
      return !!(vm?.upscaledSource === "opaque" || vm?.opaqueUrl);
    },
    // Beim nachträglichen Hintergrund-Wechsel zwischen „Original" und
    // Freisteller: wurde die jeweils andere Variante hochskaliert, liegt die
    // passende nur als 1024er-Generat in versionMeta — dann diese verwenden
    // (artworkDisplayUrl = diese Quelle bzw. das zusammengesetzte Ebenen-Bild, siehe upscaleLayersMixin)
    artworkBaseUrl() {
      const vm = this.customizationData?.versionMeta;
      if (!vm || !this.hasBackgroundLayer) return this.currentImageUrl;
      const wantsOriginal = this.wantsOriginalBg;
      if (wantsOriginal && vm.upscaledSource !== "opaque" && vm.opaqueUrl) return vm.opaqueUrl;
      if (!wantsOriginal && vm.upscaledSource === "opaque" && vm.transparentUrl) return vm.transparentUrl;
      return this.currentImageUrl;
    },
    backgroundLayerTextureUrl() {
      const bg = this.hasBackgroundLayer ? this.localBackground : null;
      if (!bg || bg.startsWith("#")) return null;
      if (this.showsOpaqueOriginal) return null;
      // preview = echtes Seitenverhältnis, lange Kante 1600 (thumbs sind nur
      // für die Auswahl-Kacheln, full nur für den Export)
      return backgroundAssetUrl(bg, "preview");
    },
    backgroundLayerTextureStyle() {
      const innerW = Math.max((this.frameRect.width || 0) - this.matPx * 2, 0);
      const innerH = Math.max((this.frameRect.height || 0) - this.matPx * 2, 0);
      const rotated = isBackgroundTextureRotated(
        this.localBackground,
        this.effectiveOrientation || "portrait"
      );
      // Bei Drehung: Box mit getauschten Maßen — deckt nach rotate(90deg)
      // exakt den Ausschnitt ab, object-fit übernimmt den cover-Beschnitt
      const boxW = rotated ? innerH : innerW;
      const boxH = rotated ? innerW : innerH;
      return {
        position: "absolute",
        width: `${boxW}px`,
        height: `${boxH}px`,
        left: `${(innerW - boxW) / 2}px`,
        top: `${(innerH - boxH) / 2}px`,
        objectFit: "cover",
        transform: rotated ? "rotate(90deg)" : undefined,
        pointerEvents: "none",
      };
    },
    artworkClipStyle() {
      const matPx = this.matPx;
      const bg = this.hasBackgroundLayer ? this.localBackground || "#ffffff" : null;
      const bgStyle = bg && bg.startsWith("#") ? { backgroundColor: bg } : {};
      return {
        position: "absolute",
        top: `${matPx}px`,
        left: `${matPx}px`,
        width: `calc(100% - ${matPx * 2}px)`,
        height: `calc(100% - ${matPx * 2}px)`,
        overflow: "hidden",
        ...bgStyle,
      };
    },
    show: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    // Basis-Konfiguration: bevorzugt savedConfig
    config() {
      return (
          this.customizationData.savedConfig ||
          this.customizationData.configuration ||
          {}
      );
    },

    updatedCustomizationConfigData() {
      return {
        ...this.config,
        orientation: this.effectiveOrientation,
        artworkCanvas: { ...this.localCanvas },
        textElements: this.localTextElements
            ? this.localTextElements.map((el) => ({ ...el }))
            : [],
        // Welche Artwork-Variante der Bearbeitung zugrunde lag — beim
        // erneuten Öffnen wird genau diese wieder vorgewählt (finalTemplate
        // ist ein gerenderter Canvas-Export und matcht keine Varianten-URL)
        sentVariant: this.selectedVariant,
        sentArtworkUrl: this.currentImageUrl || null,
        // Deckkraft der Detail-Pass-Überarbeitung je Region (nur Abweichungen von 100 %)
        upscaleRegionOpacity: { ...this.upscaleRegionOpacity },
      };
    },

    effectiveRefinementArtwork() {
      return (
          this.localRefinementArtwork ||
          this.customizationData.refinementArtwork ||
          this.refinementArtwork ||
          null
      );
    },

    configMaterial() {
      return this.config.material || "Poster";
    },
    configOrientation() {
      return this.config.orientation || "portrait";
    },
    effectiveOrientation() {
      return this.localOrientation || this.configOrientation;
    },
    configSize() {
      return this.config.size || "M";
    },

    releaseMenuEdit() {
      return this.releaseMenu.index != null ? this.aiEdits[this.releaseMenu.index] : null;
    },
    /** Aktuell gewählter KI-Edit (oder null) */
    selectedAiEdit() {
      const key = String(this.selectedVariant || "");
      if (!key.startsWith("AI_EDIT_")) return null;
      return this.aiEdits[Number(key.slice(8))] || null;
    },
    /** Persistierte + in dieser Session erzeugte KI-Edits (dedupliziert) */
    aiEdits() {
      const persisted = Array.isArray(this.customizationData?.aiEdits)
          ? this.customizationData.aiEdits
          : [];
      const known = new Set(persisted.map((e) => e.url));
      return [...persisted, ...this.localAiEdits.filter((e) => !known.has(e.url))];
    },
    /** Läuft gerade ein KI-Edit? Lokal (dieser Dialog) oder am Task
     *  persistiert (Dialog wurde zwischenzeitlich geschlossen). */
    aiEditPending() {
      return this.aiEditBusy || !!this.customizationData?.aiEditPending;
    },
    /** Irgendeine KI-Bearbeitung läuft (Region-Edit, Feedback-Edit, 4K-Edit) — die Task-Karte zeigt das als Rand */
    anyAiEditRunning() {
      return !!(this.regionEditRunning || this.feedbackEditRunning || this.aiEditPending);
    },
    pendingPromptTitle() {
      const pending = this.customizationData?.aiEditPending;
      return pending?.prompt ? `Läuft: ${pending.prompt}` : "KI-Edit läuft…";
    },
    /** KI-Textvorschlag aus dem Auto-Fix (feedback.autoEdit) — nur anbieten,
     *  wenn er zur aktuellen Element-Anzahl passt */
    aiConfigProposal() {
      // Vorschlag aus der KI-Änderung-Box hat Vorrang vor dem Auto-Fix aus AllRatings
      const local = this.localConfigProposal?.proposal;
      if (Array.isArray(local) && local.length) return local;
      const proposal = this.task?.feedback?.autoEdit?.configProposal;
      if (!Array.isArray(proposal) || !proposal.length) return null;
      return proposal;
    },
    aiProposalRequest() {
      if (this.localConfigProposal?.proposal) return this.localConfigProposal.request || "";
      return this.task?.feedback?.autoEdit?.textRequest || "";
    },
    /** Kompakte Diff-Zeilen: was ändert der KI-Vorschlag gegenüber gespeichert? */
    aiProposalDiff() {
      const proposal = this.aiConfigProposal;
      if (!proposal) return [];
      const current = this.config?.textElements || [];
      const lines = [];
      proposal.forEach((p, i) => {
        const c = current[i] || {};
        const parts = [];
        if ((p.text || "") !== (c.text || "")) parts.push(`„${c.text || "—"}“ → „${p.text || "—"}“`);
        if (p.font !== c.font) parts.push(`Font ${p.font}`);
        if (p.color !== c.color) parts.push(`Farbe ${p.color}`);
        if (Number(p.size) !== Number(c.size)) parts.push(`Größe ${c.size}% → ${p.size}%`);
        if (Number(p.y) !== Number(c.y)) parts.push(`Y ${c.y}% → ${p.y}%`);
        if (!!p.bgEnabled !== !!c.bgEnabled) parts.push(`Wolke ${p.bgEnabled ? "an" : "aus"}`);
        if (parts.length) lines.push(`Text #${i + 1}: ${parts.join(" · ")}`);
      });
      return lines;
    },
    /** Neuester Kunden-Kommentar (reine Übersetzung) — für den KI-Prompt */
    latestFeedbackComment() {
      const fb = this.task?.feedback || {};
      const comments = fb.comments;
      if (Array.isArray(comments) && comments.length) {
        const latest = comments[comments.length - 1];
        return latest.translated || latest.original || "";
      }
      return fb.commentOriginal || fb.comment || "";
    },
    /** Alle Original-Fotos (bei Kombi-Artworks beide) */
    compareOriginals() {
      return [
        this.customizationData.originalImage,
        this.customizationData.originalImage2,
      ].filter(Boolean);
    },
    validUpscales() {
      const valid = {};
      for (const [key, val] of Object.entries(this.upscaledUrls || {})) {
        const url = typeof val === "object" && val && val.url ? val.url : val;
        if (url && typeof url === "string" && url.startsWith("http")) {
          valid[key] = url;
        }
      }
      return valid;
    },

    /** Alle gespeicherten Refinement-Versionen chronologisch (inkl. Legacy-
     *  Docs, die nur refinementArtwork/At kennen) — Basis für "Refinement 1..N" */
    refinementVersionsList() {
      const list = [...(this.customizationData?.refinementVersions || [])];
      const legacy = this.customizationData?.refinementArtwork;
      if (legacy && !list.some((v) => v.url === legacy)) {
        list.push({
          url: legacy,
          at: this.customizationData?.refinementArtworkAt || Number.MAX_SAFE_INTEGER,
        });
      }
      return list.sort((a, b) => (a.at || 0) - (b.at || 0));
    },

    /** Versions-Leiste als Timeline: Basis (Original + Upscales), danach die
     *  Kette Edits↔Refinement chronologisch alt → neu. Edits tragen createdAt;
     *  das Refinement seit 25.08.26 refinementArtworkAt — Legacy-Refinements
     *  ohne Zeitstempel gelten als neueste. Pfeil-Einträge ({arrow:true})
     *  trennen die Ketten-Schritte. */
    /** Ältere Version aus der Leiste als Grundlage für Bereichs-Edits (DEFAULT = aktueller Endstand → keine) */
    editBaseVariant() {
      const key = this.selectedVariant;
      if (!key || key === "DEFAULT" || !this.upscaleJob) return null;
      const url = this.variantUrl(key);
      if (!url) return null;
      const item = this.versionBarItems.find((i) => i.key === key);
      return { key, label: item ? item.label : this.mapVariantName(key), url };
    },
    versionBarItems() {
      const items = [{ key: "ORIGINAL", label: "Original" }];
      const jobEditChain = [];
      for (const key of Object.keys(this.validUpscales)) {
        // Neue Upscale-Kette mit Detail-Pass: der Upscale VOR dem Pass ist eine eigene
        // Version, das Ergebnis (bisher „Upscale") heißt dann „Detail-Pass"
        if (key === "DEFAULT") {
          // Neue Upscale-Kette: Upscale (vor Detail-Pass) › Detail-Pass › Edit 1 › … › Edit N.
          // DEFAULT (= upscaledImage) ist immer der NEUESTE Stand: ohne Edits „Detail-Pass", sonst „Edit N".
          const edits = this.regionEdits || [];
          if (this.upscaleLayersAvailable) {
            items.push({ key: "UPSCALE_RAW", label: "Upscale", title: "Upscale vor dem Detail-Pass" });
            items.push({ arrow: true });
          }
          if (edits.length) {
            // Die Job-Edits selbst wandern mit ihrem Zeitstempel in die chronologische Kette unten
            // (#98635: ein Auto-Edit nach Refinement 3 stand sonst direkt hinter dem Detail-Pass)
            items.push({ key: "DETAIL_PASS_BASE", label: "Detail-Pass", title: "Stand vor allen Edits" });
            edits.forEach((e, i) => {
              const last = i === edits.length - 1;
              jobEditChain.push({
                key: last ? key : "JOB_EDIT_" + i,
                label: `Edit ${i + 1}`,
                title: [e.base && e.base.label ? `auf Basis von ${e.base.label}` : "", e.instruction || e.label || (last ? "Neuester Stand" : "")].filter(Boolean).join(" · "),
                at: e.at || 0,
              });
            });
          } else {
            items.push({ key, label: this.upscaleLayersAvailable ? "Detail-Pass" : this.mapVariantName(key), title: this.upscaleLayersAvailable ? "Upscale mit überarbeiteten Regionen" : "" });
          }
          continue;
        }
        items.push({ key, label: this.mapVariantName(key) });
      }

      // Kette chronologisch sortieren (Edit-Indizes bleiben stabil, nur die
      // ANZEIGE-Reihenfolge folgt den Zeitstempeln). Job-Edits ohne Zeitstempel (alt) stehen vorn.
      const chain = [...jobEditChain];
      (this.aiEdits || []).forEach((edit, index) => {
        // Edits: ohne AI_EDIT-Berechtigung nur sichtbar, wenn freigegeben
        if (!(this.canAiEdit || edit.released)) return;
        chain.push({
          key: "AI_EDIT_" + index,
          label: edit.name || "Edit " + (index + 1),
          title: edit.prompt,
          editIndex: index,
          releasedIcon: this.canAiEdit && edit.released,
          at: edit.createdAt || 0,
        });
      });
      const refinements = this.refinementVersionsList;
      if (refinements.length) {
        // Alle gespeicherten Refinements als Versionen; der neueste behält den
        // Key REFINEMENT (daran hängen Bearbeiten-Flow & effectiveRefinementArtwork)
        refinements.forEach((v, i) => {
          const isLatest = i === refinements.length - 1;
          chain.push({
            key: isLatest ? "REFINEMENT" : "REFINEMENT_V_" + i,
            label: refinements.length > 1 ? "Refinement " + (i + 1) : "Refinement",
            at: v.at || 0,
          });
        });
      } else if (this.effectiveRefinementArtwork || this.isRefinementView) {
        chain.push({
          key: "REFINEMENT",
          label: "Refinement",
          disabled: !this.localRefinementArtwork && !this.effectiveRefinementArtwork,
          at: this.customizationData?.refinementArtworkAt || Number.MAX_SAFE_INTEGER,
        });
      }
      chain.sort((a, b) => a.at - b.at);
      for (const step of chain) items.push({ arrow: true }, step);

      // Mail-Symbol: die Version, die der Kunde als Vorschau bekommen hat
      const sentKey = this.previewSentKey;
      if (sentKey) {
        const hit = items.find((i) => !i.arrow && i.key === sentKey);
        if (hit) hit.previewIcon = true;
      }

      if (this.aiEditPending && this.canAiEdit) {
        items.push({ arrow: true }, {
          key: "__AI_PENDING__",
          label: `Edit ${this.aiEdits.length + 1}…`,
          pending: true,
          disabled: true,
          title: this.pendingPromptTitle,
        });
      }
      return items;
    },

    currentImageUrl() {
      return this.variantUrl(this.selectedVariant);
    },
    targetAspectRatio() {
      return this.effectiveOrientation === "landscape" ? 3 / 2 : 2 / 3;
    },

    zoomMin() {
      return 1;
    },
    zoomMax() {
      return this.effectiveOrientation === "landscape" ? 4 : 5;
    },

    frameContainerStyle() {
      const ratio = this.targetAspectRatio;
      const cw = this.canvasRect.width;
      const ch = this.canvasRect.height;

      if (!cw || !ch) {
        const h = 70;
        const w = h * ratio;
        return {
          height: `${h}vh`,
          width: `${w}vh`,
        };
      }

      // Fläche ausnutzen: unten die schwebende Leiste (64px), oben nur 12px Luft.
      // Die Pillen oben links/rechts (je ~400px) stören nur, wenn der Rahmen so breit
      // wird, dass er unter sie ragt → dann Höhe zurücknehmen.
      const maxW = cw * 0.9;
      const fit = (maxH) => {
        let w = maxW;
        let h = w / ratio;
        if (h > maxH) {
          h = maxH;
          w = h * ratio;
        }
        return { w, h };
      };
      let { w, h } = fit(Math.max(200, ch - 84));
      if (w > cw - 2 * 400) ({ w, h } = fit(Math.max(200, ch - 150)));

      return {
        width: `${w}px`,
        height: `${h}px`,
      };
    },

    imageLayout() {
      if (!this.frameRect.width || !this.frameRect.height || !this.imageAspectRatio) {
        return null;
      }

      const matPx = this.matPx;
      const innerW = this.frameRect.width - matPx * 2;
      const innerH = this.frameRect.height - matPx * 2;

      const frameRatio = innerW / innerH;
      const imgRatio = this.imageAspectRatio;

      let baseW, baseH;
      if (imgRatio > frameRatio) {
        baseH = innerH;
        baseW = baseH * imgRatio;
      } else {
        baseW = innerW;
        baseH = baseW / imgRatio;
      }

      const currentW = baseW * this.localCanvas.scale;
      const currentH = baseH * this.localCanvas.scale;

      const translateX = this.localCanvas.x * currentW;
      const translateY = this.localCanvas.y * currentH;

      return { baseW, baseH, currentW, currentH, translateX, translateY, matPx, innerW, innerH };
    },

    artworkImageStyle() {
      const layout = this.imageLayout;

      const style = {
        position: "absolute",
        top: "0",
        left: "0",
        transformOrigin: "0 0",              // WICHTIG wie früher
        willChange: "transform",
        pointerEvents: "none",
        userSelect: "none",
      };

      if (!layout) {
        style.width = "100%";
        style.height = "100%";
        style.transform = "none";
        return style;
      }

      style.width = `${layout.baseW}px`;
      style.height = `${layout.baseH}px`;

      const rotation = this.localCanvas.rotation || 0;

      style.transform =
          `translate(${layout.translateX}px, ${layout.translateY}px)` +
          (rotation ? ` rotate(${rotation}deg)` : "") +
          ` scale(${this.localCanvas.scale})`;

      return style;
    },

    /** Kunden-Feedback: gibt es übersetzte Einträge mit abweichendem Original? (Umschalter in der Kopfzeile) */
    feedbackHasOriginals() {
      const fb = (this.task && this.task.feedback) || {};
      const entries = Array.isArray(fb.comments) && fb.comments.length ? fb.comments : fb.comment ? [{ original: fb.commentOriginal || "", translated: fb.comment }] : [];
      return entries.some((e) => e.original && e.translated && e.original !== e.translated);
    },
    /** Mitte des Vergleichs: ein gewähltes Original oder alle */
    compareShownOriginals() {
      const all = this.compareOriginals.map((url, index) => ({ url, index }));
      if (this.compareOriginalIndex === "all" || all.length <= 1) return all;
      const idx = Math.min(Number(this.compareOriginalIndex) || 0, all.length - 1);
      return all[idx] ? [all[idx]] : [];
    },
    /** Versions-Bubbles unter dem Vergleich: Leiste ohne „Original" (steht in der Mitte) und ohne Platzhalter */
    compareVersionItems() {
      const items = this.versionBarItems.filter((i) => i.arrow || (i.key !== "ORIGINAL" && !i.pending));
      // führende/doppelte Pfeile entfernen
      const out = [];
      for (const it of items) {
        if (it.arrow && (!out.length || out[out.length - 1].arrow)) continue;
        out.push(it);
      }
      while (out.length && out[out.length - 1].arrow) out.pop();
      return out;
    },
    /** Version, die als Vorschau an den Kunden ging (customizationData.previewSent) — als Leisten-Key */
    previewSentKey() {
      const ps = this.customizationData?.previewSent;
      if (!ps || (!ps.artworkUrl && !ps.variant)) return null;
      return this.resolveVariant(ps.artworkUrl || null, ps.variant || null) || null;
    },
    previewSentTitle() {
      const at = this.customizationData?.previewSent?.at;
      return at ? `Vorschau an den Kunden gesendet · ${new Date(at).toLocaleString("de-DE")}` : "Vorschau an den Kunden gesendet";
    },
    /** Land der Bestellung (Lieferland, sonst Shop-Land) für die Kopfzeile */
    taskCountryCode() {
      const t = this.task || {};
      return String(t.shippingCountryCode || t.countryCode || "").toUpperCase();
    },
    taskCountryFlag() {
      return countryFlagEmoji(this.taskCountryCode);
    },
    /** Vergleich links: Name der gewählten Version (Detail-Pass, Upscale, Edit …) */
    compareMainLabel() {
      const item = this.versionBarItems.find((i) => i.key === this.selectedVariant);
      return (item && item.label) || "Artwork";
    },
    /** Vergleich rechts: das vom Kunden gesehene Artwork — entfällt, wenn es schon links steht */
    compareCustomerArtwork() {
      const url = this.customizationData?.artworkImageUrl || null;
      return url && this.selectedVariant !== "ORIGINAL" && url !== this.currentImageUrl ? url : null;
    },
    compareTransformStyle() {
      return {
        transformOrigin: "50% 50%",
        transform: `translate(${this.compareOffset.x}px, ${this.compareOffset.y}px) scale(${this.compareZoom})`,
      };
    },
    /** Originale: Master-Transform plus eigene Abweichung */
    compareOrigTransformStyle() {
      const a = this.compareOrigAdjust;
      return {
        transformOrigin: "50% 50%",
        transform: `translate(${this.compareOffset.x + a.x}px, ${this.compareOffset.y + a.y}px) scale(${this.compareZoom * a.z})`,
      };
    },
  },
  watch: {
    anyAiEditRunning: {
      immediate: true,
      handler(running) {
        this.$emit("editStatus", { running, uid: this.task && this.task.uid });
      },
    },
    compareShowArtwork(value) {
      localStorage.setItem("ohmyCompareShowArtwork", value ? "true" : "false");
    },
    compareDialog(open) {
      if (open) {
        // Geräte-Einstellungen frisch lesen: Jede Task-Karte hat ihre eigene Dialog-Instanz, die beim Mount
        // initialisiert wurde — ohne das zeigt der nächste Task den alten Stand (Kundenansicht an/aus, Original „Alle")
        this.compareShowArtwork = localStorage.getItem("ohmyCompareShowArtwork") !== "false";
        if (this.compareOriginals.length > 1) this.compareOriginalIndex = localStorage.getItem("ohmyCompareOriginalAll") === "true" ? "all" : 0;
        this.$nextTick(() => this.scrollActiveVersionIntoView(this.$refs.compareVersionPill, false));
      }
    },
    versionBarItems() {
      this.$nextTick(() => this.scrollActiveVersionIntoView(this.$refs.versionBar, false));
    },
    compareOriginalIndex(value) {
      localStorage.setItem("ohmyCompareOriginalAll", value === "all" ? "true" : "false");
    },
    selectedVariant(value) {
      this.$nextTick(() => {
        this.scrollActiveVersionIntoView(this.$refs.versionBar);
        this.scrollActiveVersionIntoView(this.$refs.compareVersionPill);
        if (this.syncCustomBaseWithSelection) this.syncCustomBaseWithSelection();
      });
      // Format-Edits tragen ihre Orientierung — Canvas automatisch anpassen
      if (String(value).startsWith("AI_EDIT_")) {
        const edit = this.aiEdits[Number(String(value).slice(8))];
        if (edit?.orientation && edit.orientation !== this.localOrientation) {
          this.localOrientation = edit.orientation;
          this.onOrientationChange(edit.orientation);
        }
      }
    },
    value(val) {
      if (val) {
        // Geräte-Einstellung frisch lesen — andere (bereits gemountete)
        // Dialog-Instanzen haben sonst einen veralteten Stand
        this.autoNext = localStorage.getItem("ohmyLiveEditorAutoNext") !== "false";
        this.initData();

        this.$nextTick(() => {
          window.requestAnimationFrame(() => {
            this.updateMeasurements();
            this.$nextTick(() => {
              this.clampCanvas();
              this.measureTextBoxes();
            });
          });
        });
      }
    },
    // Bei jeder Änderung an Text/Font/Size/etc. die Wolkengröße neu messen
    localTextElements: {
      deep: true,
      handler() {
        this.$nextTick(() => this.measureTextBoxes());
      },
    },
  },
  created() {

  },
  mounted() {
    window.addEventListener("resize", this.onResize);
    window.addEventListener("mouseup", this.stopDrag);
    window.addEventListener("mousemove", this.onDrag);

    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("mouseup", this.stopDrag);
    window.removeEventListener("mousemove", this.onDrag);

    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);

    if (
        this.localRefinementArtwork &&
        typeof this.localRefinementArtwork === "string" &&
        this.localRefinementArtwork.startsWith("blob:")
    ) {
      try {
        URL.revokeObjectURL(this.localRefinementArtwork);
      } catch (e) {}
    }
  },
  methods: {
    downloadFileFromUrl,
    ...mapActions(["markTask", "importLiveTaskRefinement"]),

    // ===========================================================
    //  LESBARKEITS-WOLKE: gemeinsame Helfer (Vorschau + Canvas)
    // ===========================================================

    // Leitet die Wolkenfarbe automatisch aus der Textfarbe ab:
    // heller Text -> dunkle Wolke, dunkler Text -> helle Wolke,
    // mit leicht durchscheinendem Textfarbton (tint).
    getDefaultBg(color) {
      if (!color) return "#000000";
      const hex = color.replace("#", "");
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      // Wahrgenommene Helligkeit (YIQ), 0..255
      const luminance = (r * 299 + g * 587 + b * 114) / 1000;

      // Heller Text -> dunkler Hintergrund, dunkler Text -> heller Hintergrund.
      // Reiner Graustufenwert, kein Farbton.
      const target = luminance > 128 ? 0 : 255;

      const toHex = (v) => v.toString(16).padStart(2, "0");
      return `#${toHex(target)}${toHex(target)}${toHex(target)}`;
    },

    toggleMat(val) {
      // An -> Default 5%, Aus -> 0
      this.$set(this.localCanvas, "mat", val ? 5 : 0);
      this.$nextTick(() => {
        this.updateMeasurements();
        this.$nextTick(() => this.clampCanvas());
      });
    },

    onMatChange() {
      // Rand-Änderung verändert den inneren Bereich -> neu clampen
      this.$nextTick(() => {
        this.updateMeasurements();
        this.$nextTick(() => this.clampCanvas());
      });
    },

    // Wolkenfarbe: bevorzugt gespeichertes bgColor, sonst abgeleitet
    cloudColor(el) {
      return el.bgColor || this.getDefaultBg(el.color);
    },

    /** Beim Laden immer mit Auto-Wolkenfarbe starten: eine ggf. aus der
     *  Kunden-Konfiguration geerbte bgColor wird entfernt, sodass der
     *  „Auto"-Modus (hell/dunkel je Textfarbe) standardmäßig aktiv ist.
     *  Ein manueller Farbwunsch kann jederzeit im Editor gesetzt werden. */
    withCloudAutoDefault(elements) {
      return (elements || []).map((el) => ({ ...el, bgColor: null }));
    },

    // Alpha-Werte der Verlaufsstops aus der Intensität
    cloudAlpha(el) {
      const intensity = el.bgIntensity != null ? el.bgIntensity : DEFAULT_BG_INTENSITY;
      return { core: intensity, mid: intensity * 0.9 };
    },

    onToggleBg(index, val) {
      this.$set(this.localTextElements[index], "bgEnabled", val);
      // Beim Aktivieren immer mit der Auto-Logik starten (hell/dunkel je Textfarbe)
      if (val) this.$set(this.localTextElements[index], "bgColor", null);
      // Erst nach dem nächsten Paint messen, sonst sind die Maße noch 0/stale
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          this.measureTextBoxes();
        });
      });
    },

    // Ellipsen-Radien aus der gemessenen Textbox (gleiche Faktoren wie React/SVG)
    cloudDims(idx) {
      const box = this.textBoxes[idx] || { w: 0, h: 0 };
      return {
        rx: (box.w / 2) * 1.35,
        ry: (box.h / 2) * 1.7,
      };
    },

    showCloud(element, idx) {
      // liest textBoxes als Ganzes -> reaktiv
      const box = this.textBoxes[idx]
      return !!(element.bgEnabled && box && box.w > 0)
    },

    // "#rrggbb" + alpha(0..1) -> "rgba(r,g,b,a)" (für Canvas-Gradient)
    hexWithAlpha(hex, alpha) {
      const h = (hex || "#000000").replace("#", "");
      const r = parseInt(h.substring(0, 2), 16);
      const g = parseInt(h.substring(2, 4), 16);
      const b = parseInt(h.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },

    // Misst die gerenderte Größe jedes Text-Spans für die SVG-Wolke
    async measureTextBoxes() {
      if (!Array.isArray(this.localTextElements)) return;

      await Promise.all(
          this.localTextElements.map(async (el, idx) => {
            if (!el) return;

            // Gezielt die verwendete Font laden (Größe ist für load() egal,
            // aber Format "<size> <family>" ist Pflicht)
            if (el.font && document.fonts && document.fonts.load) {
              try {
                await document.fonts.load(`16px "${el.font}"`);
                // Emoji-Schrift nur laden, wenn der Text Emojis enthält (zweites Argument)
                if (this.emojiFont === "ohmy") {
                  await document.fonts.load('16px "Ohmy Emoji"', el.text || "");
                  // Symbol-Schrift (♡ ★ ♪ …) ebenso nur bei Bedarf
                  await document.fonts.load('16px "Ohmy Symbols"', el.text || "");
                }
              } catch (e) {
                // ignorieren – wir messen trotzdem
              }
            }

            const refEl = this.$refs["textMeasure" + idx];
            const node = Array.isArray(refEl) ? refEl[0] : refEl;
            if (node) {
              const r = node.getBoundingClientRect();
              this.$set(this.textBoxes, idx, { w: r.width, h: r.height });
            }
          })
      );
    },
    onOrientationChange() {
      // Nur wenn der User im Editor das Format wechselt,
      // NICHT beim initialen Laden aus der Config.
      this.localCanvas = {
        ...this.localCanvas,
        x: 0,
        y: 0,
        scale: 1,
      };
      this.zoomProxy = 1;

      this.$nextTick(() => {
        this.updateMeasurements();
        this.$nextTick(() => this.clampCanvas());
      });
    },

    onKeyDown(e) {
      if (!this.show) return;
      if (e.key !== "y" && e.key !== "Y") return;

      const tag = e.target.tagName;
      const isInput =
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          e.target.isContentEditable;

      if (isInput) return;
      if (this.dialogImage) return;
      if (this.compareDialog) return (this.compareDialog = false);

      if (this.currentImageUrl) {
        this.isKeyboardZoom = true;
        this.openCompareDialog();
      }
    },

    onKeyUp(e) {
      if (!this.show) return;
      if (e.key !== "y" && e.key !== "Y") return;

      const tag = e.target.tagName;
      const isInput =
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          e.target.isContentEditable;

      if (isInput) return;

      this.isKeyboardZoom = false;
    },

    openRevisionDialog(isAdminRevision) {
      this.isAdminRevision = isAdminRevision;
      this.revisionComment = "";
      this.showRevisionDialog = true;
    },

    initData() {
      const config = this.config || {};
      const canvasCfg = config.artworkCanvas || {};

      let rotation = 0;
      if (typeof canvasCfg.rotation === "number") {
        let r = canvasCfg.rotation;
        r = r % 360;
        if (r > 180) r -= 360;
        if (r < -180) r += 360;
        rotation = r;
      }

      this.localCanvas = {
        x: typeof canvasCfg.x === "number" ? canvasCfg.x : 0,
        y: typeof canvasCfg.y === "number" ? canvasCfg.y : 0,
        scale: typeof canvasCfg.scale === "number" ? canvasCfg.scale : 1,
        mat: typeof canvasCfg.mat === "number" ? canvasCfg.mat : 0,
        rotation,
      };

      this.zoomProxy = this.localCanvas.scale || 1;
      this.localOrientation = config.orientation || this.configOrientation;

      this.localTextElements = config.textElements
          ? this.withCloudAutoDefault(JSON.parse(JSON.stringify(config.textElements)))
          : [];

      this.localBackground = config.background?.value
          || (this.hasBackgroundLayer ? "bg00021" : null);

      this.restoreUpscaleRegionOpacity(config);
      this.compareTab = "overview";

      // Auto-Fix-Textvorschlag als Default aktivieren — der Umschalter
      // "Aktuell" stellt den gespeicherten Stand mit einem Klick wieder her
      this.aiProposalActive = false;
      this.aiProposalBackup = null;
      if (this.aiConfigProposal) {
        this.applyAiProposal(true);
      }

      this.textBoxes = {};

      this.localRefinementArtwork = null;

      if (this.effectiveRefinementArtwork) {
        this.selectedVariant = "REFINEMENT";
      } else if (this.upscaledUrls["DEFAULT"] && (this.upscaledUrls["DEFAULT"].url || typeof this.upscaledUrls["DEFAULT"] === "string")) {
        // Nur wenn es wirklich ein Upscale gibt — nach einem fehlgeschlagenen Upscale-Job (z. B. Nacktheit)
        // ist upscaledImage null; das Objekt { loading, url } ist trotzdem da
        this.selectedVariant = "DEFAULT";
      } else {
        this.selectedVariant = "ORIGINAL";
      }

      // Tatsächlich gesendete Variante wiederherstellen: bevorzugt über die
      // Quell-URL (robust gegen Index-Verschiebungen), sonst über den Key
      const sentResolved = this.resolveVariant(config.sentArtworkUrl, config.sentVariant);
      if (sentResolved) this.selectedVariant = sentResolved;

      // Auto-Fix-Bild-Edit vorwählen — der frische KI-Fix zur Beschwerde
      // gewinnt gegenüber der (beanstandeten) gesendeten Variante
      const autoEditUrl = this.task?.feedback?.autoEdit?.editUrl;
      if (autoEditUrl) {
        const autoEditIndex = this.aiEdits.findIndex(e => e.url === autoEditUrl);
        if (autoEditIndex >= 0 && (this.canAiEdit || this.aiEdits[autoEditIndex].released)) {
          this.selectedVariant = "AI_EDIT_" + autoEditIndex;
        }
      }

      this.openPanels = [];
      this.openArtworkPanels = [];

      this.resetCompareView();
    },

    /** Variante aus gespeicherter Quell-URL bzw. Varianten-Key auflösen —
     *  nur wenn sie am aktuellen Task noch existiert, sonst null */
    resolveVariant(url, variantKey) {
      // Upscale vor dem Detail-Pass (neue Upscale-Kette)
      if (this.upscaleLayersAvailable && (variantKey === "UPSCALE_RAW" || (url && url === this.customizationData.upscaleRawImage))) {
        return "UPSCALE_RAW";
      }
      if (url) {
        const editIndex = this.aiEdits.findIndex((e) => e.url === url);
        if (editIndex >= 0 && (this.canAiEdit || this.aiEdits[editIndex].released)) {
          return "AI_EDIT_" + editIndex;
        }
        if (this.effectiveRefinementArtwork === url) return "REFINEMENT";
        // Historische Refinement-Version (Verlauf "Refinement 1..N")
        const refIndex = this.refinementVersionsList.findIndex((v) => v.url === url);
        if (refIndex >= 0) {
          return refIndex === this.refinementVersionsList.length - 1
              ? "REFINEMENT"
              : "REFINEMENT_V_" + refIndex;
        }
        // Edit-Versionen der Upscale-Kette: Die Vorschau ging z. B. nach dem Detail-Pass raus, danach kam ein Edit —
        // dann zeigt previewSent.artworkUrl auf edits[0].previous.final (Detail-Pass), nicht auf den neuesten Stand
        const jobEdits = this.regionEdits || [];
        if (jobEdits.length) {
          const first = jobEdits[0];
          if (first && first.previous && first.previous.final && first.previous.final.url === url) return "DETAIL_PASS_BASE";
          const ei = jobEdits.findIndex((e) => e && e.final && e.final.url === url);
          if (ei >= 0) return ei === jobEdits.length - 1 ? "DEFAULT" : "JOB_EDIT_" + ei;
        }
        const upscaleEntry = Object.entries(this.validUpscales).find(
            ([, u]) => u === url
        );
        if (upscaleEntry) return upscaleEntry[0];
        if (
            url === this.customizationData.artworkImageUrl ||
            url === this.customizationData.originalImage
        ) {
          return "ORIGINAL";
        }
      }
      if (variantKey) {
        const key = String(variantKey);
        if (key.startsWith("AI_EDIT_")) {
          const keyEdit = this.aiEdits[Number(key.slice(8))];
          if (keyEdit && (this.canAiEdit || keyEdit.released)) return key;
        }
        if (key === "REFINEMENT" && this.effectiveRefinementArtwork) return key;
        if (key.startsWith("REFINEMENT_V_")) {
          const i = Number(key.slice(13));
          if (this.refinementVersionsList[i]) return key;
        }
        if (this.validUpscales[key]) return key;
        if (key === "ORIGINAL") return key;
      }
      return null;
    },

    updateMeasurements() {
      this.measureCanvas();
      this.$nextTick(() => {
        this.measureFrame();
        this.$nextTick(() => this.measureTextBoxes());
      });
    },

    async downloadArtwork() {
      await downloadImageFromUrl(
          this.currentImageUrl,
          `${this.task.orderName}_${this.mapVariantName(this.selectedVariant)}`,
          () => {},
          "jpg"
      );
    },

    measureCanvas() {
      if (this.$refs.canvasRef) {
        const rect = this.$refs.canvasRef.getBoundingClientRect();
        this.canvasRect = { width: rect.width, height: rect.height };
      }
    },

    measureFrame() {
      if (this.$refs.frameRef) {
        const rect = this.$refs.frameRef.getBoundingClientRect();
        this.frameRect = { width: rect.width, height: rect.height };
      }
    },

    onResize() {
      this.updateMeasurements();
    },

    onImageLoad(e) {
      const { naturalWidth, naturalHeight } = e.target;
      if (naturalHeight > 0) {
        this.imageAspectRatio = naturalWidth / naturalHeight;
      }

      if (!this.show) return; // falls v-show den Dialog gerade versteckt hält

      this.$nextTick(() => {
        this.updateMeasurements();
        this.$nextTick(() => this.clampCanvas());
      });
    },


    mapVariantName(key) {
      const map = { DEFAULT: "Upscale", PRO_CAR: "Car", PRO_PERSON: "Person" };
      return map[key] || key;
    },

    getTextStyle(text) {
      const frameW = this.frameRect.width || 500;
      const sizePx = (frameW * text.size) / 100;

      return {
        position: "absolute",
        left: `${text.x}%`,
        top: `${text.y}%`,
        transform: "translate(-50%, -50%)",
        // Emojis/Sonderzeichen: Geräte-Schrift oder unsere — Schalter „Emojis" über den Texten
        fontFamily: textFontStack(text.font, this.emojiFont),
        fontSize: `${sizePx}px`,
        color: text.color,
        lineHeight: 1.1,
        whiteSpace: "nowrap",
        cursor: "move",
        pointerEvents: "auto",
        userSelect: "none",
      };
    },

    resetTexts() {
      const cfg = this.config || {};
      this.localTextElements = this.withCloudAutoDefault(
          JSON.parse(JSON.stringify(cfg.textElements || []))
      );
      this.aiProposalActive = false;
      this.aiProposalBackup = null;
    },

    /** KI-Textvorschlag ein-/ausschalten. Beim Einschalten wird der
     *  Ist-Stand gesichert und beim Zurückschalten exakt wiederhergestellt —
     *  ein schlechter Vorschlag kostet so nur einen Klick. */
    applyAiProposal(active) {
      if (active === this.aiProposalActive || !this.aiConfigProposal) return;
      if (active) {
        this.aiProposalBackup = JSON.parse(JSON.stringify(this.localTextElements));
        this.localTextElements = JSON.parse(JSON.stringify(this.aiConfigProposal));
      } else if (this.aiProposalBackup) {
        this.localTextElements = JSON.parse(JSON.stringify(this.aiProposalBackup));
      }
      this.aiProposalActive = active;
    },

    deleteTexts() {
      this.localTextElements = this.localTextElements.map((tEl) => {
        return { ...tEl, text: "" };
      });
    },

    openColorPicker(index) {
      const ref = this.$refs["cp" + index];
      const node = Array.isArray(ref) ? ref[0] : ref;
      if (node && typeof node.click === "function") {
        node.click();
      }
    },

    // --- DRAG ARTWORK ---
    startArtworkDrag(e) {
      if (!this.imageLayout) return;
      if (this.dragMode === "text") return;
      this.dragMode = "artwork";
      this.dragStart = { x: e.clientX, y: e.clientY };
      this.dragStartCanvas = { ...this.localCanvas };
    },

    dragArtwork(e) {
      if (this.dragMode !== "artwork" || !this.imageLayout) return;

      const layout = this.imageLayout;
      const dxPx = e.clientX - this.dragStart.x;
      const dyPx = e.clientY - this.dragStart.y;

      const dXPct = dxPx / layout.currentW;
      const dYPct = dyPx / layout.currentH;

      this.localCanvas.x = this.dragStartCanvas.x + dXPct;
      this.localCanvas.y = this.dragStartCanvas.y + dYPct;

      this.clampCanvas();
    },

    /** Klick auf einen Text im Canvas → Tab „Text & Artwork" öffnen, das
     *  passende Panel aufklappen und das Textfeld fokussieren. Nach einem
     *  echten Verschieben (Drag) unterdrückt, damit Repositionieren nicht
     *  ungewollt in die Sidebar springt. */
    onTextClick(index) {
      if (this.textWasDragged) return;
      this.sidebarTab = "design";
      const alreadyOpen = this.openPanels.includes(index);
      if (!alreadyOpen) {
        this.openPanels = [...this.openPanels, index];
      }

      const resolveRef = (name) => {
        const ref = this.$refs[name + index];
        return Array.isArray(ref) ? ref[0] : ref;
      };
      const scrollToPanel = () => {
        const panel = resolveRef("textPanel");
        const el = panel && panel.$el ? panel.$el : null;
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      };
      const focusField = () => {
        const field = resolveRef("textInput");
        // preventScroll: der Browser soll das Scrollen NICHT an sich reißen —
        // die Position bestimmt scrollToPanel()
        if (field && field.focus) field.focus({ preventScroll: true });
      };

      this.$nextTick(() => {
        // Sofort grob hinscrollen (Panel-Header sitzt stabil, auch während sich
        // der Inhalt noch aufklappt) und fokussieren …
        scrollToPanel();
        focusField();
        // … und nach der Aufklapp-Animation (~300 ms) final zentrieren, damit
        // das ganze Panel inkl. Textfeld sauber im Sichtfeld liegt.
        if (!alreadyOpen) {
          setTimeout(scrollToPanel, 320);
        }
      });
    },

    // --- DRAG TEXT ---
    startTextDrag(e, index) {
      if (!this.frameRect.width || !this.localTextElements[index]) return;
      this.dragMode = "text";
      this.textWasDragged = false;
      this.dragTextIndex = index;
      this.dragStart = { x: e.clientX, y: e.clientY };
      this.dragStartTextPos = {
        x: this.localTextElements[index].x,
        y: this.localTextElements[index].y,
      };
    },

    dragText(e) {
      if (this.dragMode !== "text") return;
      const idx = this.dragTextIndex;
      if (idx == null || !this.localTextElements[idx]) return;

      const dxPx = e.clientX - this.dragStart.x;
      const dyPx = e.clientY - this.dragStart.y;

      // Ab ein paar Pixeln als echtes Verschieben werten (unterdrückt den
      // Fokus-Sprung des Klicks danach)
      if (Math.abs(dxPx) > 3 || Math.abs(dyPx) > 3) this.textWasDragged = true;

      const frameW = this.frameRect.width || 1;
      const frameH = this.frameRect.height || 1;

      const dXPct = (dxPx / frameW) * 100;
      const dYPct = (dyPx / frameH) * 100;

      const el = this.localTextElements[idx];
      let newX = this.dragStartTextPos.x + dXPct;
      let newY = this.dragStartTextPos.y + dYPct;

      const SNAP_THRESHOLD = 2;
      if (Math.abs(newX - 50) < SNAP_THRESHOLD) {
        newX = 50;
      }
      if (Math.abs(newY - 50) < SNAP_THRESHOLD) {
        newY = 50;
      }

      el.x = newX;
      el.y = newY;
    },

    onDrag(e) {
      if (!this.dragMode) return;
      if (this.dragMode === "artwork") {
        this.dragArtwork(e);
      } else if (this.dragMode === "text") {
        this.dragText(e);
      }
    },

    stopDrag() {
      this.dragMode = null;
      this.dragTextIndex = null;
    },

    // --- DRAG & DROP CUSTOM IMAGE ---
    onFrameDragOver(e) {
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = "copy";
      }
    },
    onFrameDragEnter() {
      this.isFrameDragOver = true;
    },
    onFrameDragLeave(e) {
      if (
          e.currentTarget &&
          e.relatedTarget &&
          e.currentTarget.contains(e.relatedTarget)
      )
        return;
      this.isFrameDragOver = false;
    },
    onFrameDrop(e) {
      this.isFrameDragOver = false;
      const dt = e.dataTransfer;
      if (!dt) return;

      if (dt.files && dt.files.length) {
        const file = Array.from(dt.files).find(
            (f) => f.type && f.type.startsWith("image/")
        );
        if (file) {
          const objectUrl = URL.createObjectURL(file);
          this.applyCustomImage(objectUrl);
          return;
        }
      }

      if (dt.items && dt.items.length) {
        for (let i = 0; i < dt.items.length; i++) {
          const item = dt.items[i];
          if (item.kind === "string" && item.type === "text/uri-list") {
            item.getAsString((url) => {
              if (url) {
                this.applyCustomImage(url.trim());
              }
            });
            return;
          }
        }
      }
    },
    applyCustomImage(url) {
      if (
          this.localRefinementArtwork &&
          typeof this.localRefinementArtwork === "string" &&
          this.localRefinementArtwork.startsWith("blob:")
      ) {
        try {
          URL.revokeObjectURL(this.localRefinementArtwork);
        } catch (e) {}
      }
      this.localRefinementArtwork = url;
      this.selectedVariant = "REFINEMENT";
    },

    clampCanvas() {
      const layout = this.imageLayout;
      if (!layout || !this.frameRect.width || !this.frameRect.height) return;

      const innerW = layout.innerW;
      const innerH = layout.innerH;

      const currentW = layout.currentW;
      const currentH = layout.currentH;

      const minXPct = (innerW - currentW) / currentW;
      const maxXPct = 0;
      const minYPct = (innerH - currentH) / currentH;
      const maxYPct = 0;

      let x = this.localCanvas.x;
      let y = this.localCanvas.y;

      if (x < minXPct) x = minXPct;
      if (x > maxXPct) x = maxXPct;
      if (y < minYPct) y = minYPct;
      if (y > maxYPct) y = maxYPct;

      this.localCanvas.x = x;
      this.localCanvas.y = y;
    },

    // --- ZOOM ---
    handleZoom(newScale, oldScale) {
      if (
          !this.frameRect.width ||
          !this.frameRect.height ||
          !this.imageAspectRatio
      ) return;
      if (newScale === oldScale) return;

      const frameW = this.frameRect.width;
      const frameH = this.frameRect.height;
      const frameRatio = frameW / frameH;
      const imgRatio = this.imageAspectRatio;

      let baseW, baseH;
      if (imgRatio > frameRatio) {
        baseH = frameH;
        baseW = baseH * imgRatio;
      } else {
        baseW = frameW;
        baseH = baseW / imgRatio;
      }

      const currentW = baseW * oldScale;
      const currentH = baseH * oldScale;

      const ratio = newScale / oldScale;

      const currentPixelX = this.localCanvas.x * currentW;
      const currentPixelY = this.localCanvas.y * currentH;

      const newPixelX = currentPixelX * ratio + (frameW / 2) * (1 - ratio);
      const newPixelY = currentPixelY * ratio + (frameH / 2) * (1 - ratio);

      const newW = currentW * ratio;
      const newH = currentH * ratio;

      const newX = newPixelX / newW;
      const newY = newPixelY / newH;

      this.localCanvas = {
        ...this.localCanvas,
        x: newX,
        y: newY,
        scale: newScale,
      };

      this.$nextTick(() => {
        this.clampCanvas();
        this.zoomProxy = this.localCanvas.scale;
      });
    },

    stepZoom(delta) {
      this.onSliderZoom((this.localCanvas.scale || 1) + delta);
    },
    onSliderZoom(value) {
      const v = parseFloat(value);
      const newScale = Math.min(Math.max(v, this.zoomMin), this.zoomMax);
      const oldScale = this.localCanvas.scale || 1;
      this.handleZoom(newScale, oldScale);
    },

    onWheel(e) {
      const delta = e.deltaY > 0 ? -0.05 : 0.05;
      let next = (this.localCanvas.scale || 1) + delta;
      next = Math.min(Math.max(next, this.zoomMin), this.zoomMax);
      this.handleZoom(next, this.localCanvas.scale || 1);
    },

    loadImage(url) {
      return new Promise((resolve, reject) => {
        const img = new Image();

        if (typeof url === "string" && url.startsWith("http")) {
          img.crossOrigin = "anonymous";
        }

        img.onload = () => resolve(img);
        img.onerror = (e) => {
          console.error("Image load error", e);
          reject(e);
        };
        img.src = url;
      });
    },

    canvasToBlob(canvas, type = "image/jpeg", quality = 0.9) {
      return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
              if (blob) resolve(blob);
              else reject(new Error("Failed to create blob from canvas"));
            },
            type,
            quality
        );
      });
    },

    generateShortId() {
      return Math.random().toString(36).substr(2, 6);
    },

    async uploadToStorage(blob, path) {
      const storage = getStorage();
      const storageRef = ref(storage, path);
      const uploadResponse = await uploadBytes(storageRef, blob);
      return await getDownloadURL(uploadResponse.ref);
    },

    // *** WICHTIG: Render-Logik = Editor-Logik ***
    renderArtworkToCanvas(ctx, img, frameW, frameH) {
      // img ist ein Bild oder (zusammengesetzte Upscale-Ebenen) ein Canvas
      const imgW = img.naturalWidth || img.width;
      const imgH = img.naturalHeight || img.height;
      if (!imgW || !imgH) return;

      // Passepartout (kürzere Seite, wie im Editor)
      const matPx = (Math.min(frameW, frameH) * (this.localCanvas.mat || 0)) / 100;
      const innerW = frameW - matPx * 2;
      const innerH = frameH - matPx * 2;

      const imgRatio = imgW / imgH;
      const frameRatio = innerW / innerH;

      let baseScale;
      if (imgRatio > frameRatio) {
        baseScale = innerH / imgH;
      } else {
        baseScale = innerW / imgW;
      }

      const scale = this.localCanvas.scale || 1;
      const finalScale = baseScale * scale;

      const scaledW = imgW * finalScale;
      const scaledH = imgH * finalScale;

      // Offsets relativ zum inneren Bereich
      const offsetX = matPx + this.localCanvas.x * scaledW;
      const offsetY = matPx + this.localCanvas.y * scaledH;

      const angleDeg = this.localCanvas.rotation || 0;
      const angleRad = (angleDeg * Math.PI) / 180;

      ctx.save();

      // Auf den inneren Bereich clippen, damit nichts in den Rand läuft
      if (matPx > 0) {
        ctx.beginPath();
        ctx.rect(matPx, matPx, innerW, innerH);
        ctx.clip();
      }

      ctx.translate(offsetX, offsetY);
      if (angleRad !== 0) {
        ctx.rotate(angleRad);
      }
      ctx.scale(finalScale, finalScale);
      ctx.drawImage(img, 0, 0);

      ctx.restore();
    },

    // Zeichnet Texte inkl. Lesbarkeits-Wolke auf den finalen Canvas.
    // Die Wolke nutzt createRadialGradient (Kreis -> per Skalierung Ellipse)
    // und denselben Verlauf/dieselben Faktoren wie die SVG-Vorschau.
    renderTextsToCanvas(ctx, frameW, frameH) {
      if (!Array.isArray(this.localTextElements)) return;

      this.localTextElements.forEach((el) => {
        if (!el || !el.text) return;

        const fontSizePx = (frameW * el.size) / 100;
        const x = (el.x / 100) * frameW;
        const y = (el.y / 100) * frameH;

        ctx.save();
        // Emojis/Sonderzeichen: Geräte-Schrift oder unsere — Schalter „Emojis" über den Texten
        ctx.font = `${fontSizePx}px ${textFontStack(el.font, this.emojiFont)}`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        // --- Lesbarkeits-Wolke (vor dem Text) ---
        if (el.bgEnabled) {
          const metrics = ctx.measureText(el.text);
          const textW = metrics.width;
          const ascent = metrics.actualBoundingBoxAscent || fontSizePx * 0.7;
          const descent = metrics.actualBoundingBoxDescent || fontSizePx * 0.3;
          const textH = ascent + descent;

          const rx = (textW / 2) * 1.6;
          const ry = (textH / 2) * 2.2;

          if (rx > 0 && ry > 0) {
            const bgColor = this.cloudColor(el);
            const { core, mid } = this.cloudAlpha(el);

            ctx.save();
            // weicher Blur wie in der Vorschau (modern browsers)
            ctx.filter = `blur(${frameW * 0.015}px)`;
            // Kreis (r = ry) horizontal zu Ellipse (rx, ry) skalieren
            ctx.translate(x, y);
            ctx.scale(rx / ry, 1);

            const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, ry);
            grad.addColorStop(0.0, this.hexWithAlpha(bgColor, core));
            grad.addColorStop(0.2, this.hexWithAlpha(bgColor, core));
            grad.addColorStop(0.45, this.hexWithAlpha(bgColor, mid));
            grad.addColorStop(0.75, this.hexWithAlpha(bgColor, mid * 0.4));
            grad.addColorStop(1.0, this.hexWithAlpha(bgColor, 0));

            ctx.fillStyle = grad;
            ctx.beginPath();
            ctx.arc(0, 0, ry, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore(); // setzt filter + transform zurück
          }
        }

        // --- Text darüber ---
        ctx.fillStyle = el.color || "#000000";
        ctx.fillText(el.text, x, y);
        ctx.restore();
      });
    },

    resetArtworkTransform() {
      this.localCanvas.x = 0;
      this.localCanvas.y = 0;
      this.localCanvas.scale = 1;
      this.localCanvas.rotation = 0;
      this.zoomProxy = 1;
      this.$nextTick(() => this.clampCanvas());
    },

    rotateRight90() {
      let angle = this.localCanvas.rotation || 0;
      const step = 90;
      let next = Math.ceil(angle / step) * step;
      if (next === angle) {
        next += step;
      }
      if (next > 180) {
        next = -180;
      }
      this.localCanvas.rotation = next;
    },

    rotateLeft90() {
      let angle = this.localCanvas.rotation || 0;
      const step = 90;
      let prev = Math.floor(angle / step) * step;
      if (prev === angle) {
        prev -= step;
      }
      if (prev < -180) {
        prev = 180;
      }
      this.localCanvas.rotation = prev;
    },

    persistAutoNext(value) {
      localStorage.setItem("ohmyLiveEditorAutoNext", value ? "true" : "false");
    },
    onFulfillClick() {
      if (this.isSending) return;
      if (!this.fulfillArmed) {
        // Klick 1: scharf stellen, nach 4s automatisch entschärfen
        this.fulfillArmed = true;
        clearTimeout(this.fulfillArmTimer);
        this.fulfillArmTimer = setTimeout(() => (this.fulfillArmed = false), 4000);
        return;
      }
      // Klick 2: bestätigt — wirklich fulfillen
      clearTimeout(this.fulfillArmTimer);
      this.fulfillArmed = false;
      this.fulfillWasConfirmed = true;
      this.sendToFulfill(false);
    },
    setCloudColor(index, value) {
      // null = zurück zur Auto-Logik (hell/dunkel je Textfarbe)
      this.$set(this.localTextElements[index], "bgColor", value || null);
    },
    openCloudColorPicker(index) {
      const input = this.$refs["cloudcp" + index];
      const el = Array.isArray(input) ? input[0] : input;
      if (el) el.click();
    },
    async copyOrderNote() {
      try {
        await navigator.clipboard.writeText(this.task.orderNote || "");
        this.orderNoteCopied = true;
        setTimeout(() => (this.orderNoteCopied = false), 1500);
      } catch (e) {
        console.error("Kopieren fehlgeschlagen", e);
      }
    },
    /** Rechtsklick in der Versions-Leiste: Freigabe-Menü nur für Edit-Chips
     *  (auf anderen Chips bleibt das Browser-Kontextmenü) */
    onVersionChipContextMenu(e, item) {
      if (item.editIndex == null) return;
      e.preventDefault();
      this.openReleaseMenu(e, item.editIndex);
    },
    /** Rechtsklick auf einen Edit: Freigabe-Menü (nur mit AI_EDIT-Recht) */
    openReleaseMenu(e, index) {
      if (!this.canAiEdit) return;
      this.releaseMenu = { show: false, x: e.clientX, y: e.clientY, index };
      this.$nextTick(() => (this.releaseMenu.show = true));
    },
    /** Freigabe-Flag am Edit togglen und die komplette Edit-Liste am Task
     *  persistieren (tasks + shopifyTasks) — Nutzer ohne AI_EDIT sehen
     *  ausschließlich freigegebene Edits. */
    async toggleReleaseEdit() {
      const edit = this.releaseMenuEdit;
      if (!edit) return;
      await this.setEditReleased(edit, !edit.released);
    },
    async setEditReleased(edit, released) {
      this.$set(edit, "released", released);
      const arr = this.aiEdits.map((e) => ({ ...e }));
      const uid = String(this.task.uid);
      await Promise.all(
        ["tasks", "shopifyTasks"].map((col) =>
          setDoc(
            doc(db, col, uid),
            { customizationData: { aiEdits: arr } },
            { merge: true }
          ).catch(() => {})
        )
      );
    },
    /** Prompt + upscaltes Artwork + Original(e) -> gpt-image-2 -> 4K.
     *  Ergebnis wird serverseitig am Task persistiert (aiEdits-Array) —
     *  der Dialog darf währenddessen geschlossen werden. */
    /**
     * KI-Änderung-Box: freien Wunsch wie den Auto-Edit einordnen. Betrifft er den Overlay-Text (Name, Widmung,
     * Schrift, Position), entsteht ein KI-Textvorschlag im Design-Tab; betrifft er das Bild, läuft der Feedback-Edit.
     * Gemischt: beides. Weder noch: Hinweis mit 4K-Rückfall. Scheitert die Einordnung, läuft der Bild-Weg wie bisher.
     */
    /** Edit zurücknehmen (Kachel-Löschen): spätere Edits fallen mit — danach ist DEFAULT der Stand davor */
    async removeJobEdit(index) {
      const total = (this.regionEdits || []).length;
      const later = total - 1 - index;
      if (later > 0 && !window.confirm(`Edit ${index + 1} zurücknehmen? Die ${later} späteren Edits werden ebenfalls entfernt.`)) return;
      await this.revertRegionEdit(index);
      this.selectedVariant = "DEFAULT";
    },
    async runSmartEdit(text) {
      const prompt = String(text || "").trim();
      if (!prompt || this.smartClassifying) return;
      this.aiEditPrompt = "";
      this.smartClassifying = true;
      let verdict = null;
      try {
        const fn = httpsCallable(fbFunctions, "classifyEditRequest", { timeout: 60000 });
        const { data } = await fn({ text: prompt, textElements: this.localTextElements || [], productHint: this.task?.productName || "" });
        verdict = data || null;
      } catch (e) {
        console.warn("classifyEditRequest fehlgeschlagen, Bild-Weg direkt:", e);
      } finally {
        this.smartClassifying = false;
      }
      if (!verdict) return this.startSmartEdit(prompt);
      if (verdict.textOverlay) {
        if (verdict.configProposal) {
          this.localConfigProposal = { proposal: verdict.configProposal, request: verdict.textRequest || prompt };
          this.sidebarTab = "design";
          EventBus.$emit(events.SNACKBAR, { message: "KI-Textvorschlag bereit (Design-Tab: Aktuell / KI-Vorschlag)", type: "success" });
        } else {
          EventBus.$emit(events.SNACKBAR, { message: `Textwunsch erkannt, aber nicht umsetzbar: ${verdict.textRequest || prompt}`, type: "warning" });
        }
      }
      if (verdict.imageEdit) return this.startSmartEdit(prompt);
      if (!verdict.textOverlay) this.smartEditUnclear = { prompt, reason: "weder Bild- noch Textwunsch erkannt" };
    },
    async runAiEditCall({ prompt, targetOrientation = null }) {
      this.aiEditBusy = true;
      this.aiEditError = "";
      try {
        // Bearbeitungsgrundlage = die im Editor AUSGEWÄHLTE Version
        // (Refinement/Edit/Upscale/Original) — Fallback aufs Upscale
        const artworkUrl =
            this.currentImageUrl ||
            this.validUpscales.DEFAULT ||
            this.customizationData.artworkImageUrl;
        const editArtworkLive = httpsCallable(fbFunctions, "editArtworkLive", {
          timeout: 540_000, // Function läuft bis zu 9 Min — Client-Default (70s) reicht nicht
        });
        const { data } = await editArtworkLive({
          taskUid: this.task.uid?.toString(),
          prompt,
          artworkUrl,
          originalUrls: this.compareOriginals,
          targetOrientation,
        });
        if (data?.ok && data.edit) {
          this.localAiEdits.push(data.edit);
          this.selectedVariant = "AI_EDIT_" + (this.aiEdits.length - 1);
          // Formatwechsel-Edit: Canvas-Ausrichtung automatisch mitziehen
          if (data.edit.orientation && data.edit.orientation !== this.localOrientation) {
            this.localOrientation = data.edit.orientation;
            this.onOrientationChange(data.edit.orientation);
          }
          return true;
        }
        this.aiEditError = data?.error || "Bearbeitung fehlgeschlagen";
        return false;
      } catch (e) {
        console.error("KI-Änderung fehlgeschlagen:", e);
        this.aiEditError = e.message || "Bearbeitung fehlgeschlagen";
        return false;
      } finally {
        this.aiEditBusy = false;
      }
    },
    async runAiEdit() {
      if (!this.aiEditPrompt || this.aiEditPending) return;
      const prompt = this.aiEditPrompt;
      this.aiEditPrompt = "";
      const call = this.runAiEditCall({ prompt });
      // Nutzer wartet ohnehin nicht auf den Edit — direkt zum nächsten Task
      if (this.autoNext) this.$emit("taskCompleted", { autoNext: true });
      await call;
    },
    /** Illustration per KI ins jeweils andere Format bringen (Outpainting) */
    async runAiReformat() {
      if (this.aiEditPending) return;
      const target = this.localOrientation === "portrait" ? "landscape" : "portrait";
      const prompt =
          `Convert this watercolor artwork to ${target} orientation. ` +
          "Extend the watercolor background, splashes and edges naturally to fill the new canvas. " +
          "Keep the subject, faces, colors, text and painting style exactly unchanged.";
      const call = this.runAiEditCall({ prompt, targetOrientation: target });
      if (this.autoNext) this.$emit("taskCompleted", { autoNext: true });
      await call;
    },
    confirmRevision() {
      // Überarbeitung mit gewähltem, nicht freigegebenem KI-Edit: erst fragen,
      // ob er freigegeben werden soll — sonst sieht der Überarbeiter ohne
      // AI_EDIT-Recht das gewählte Bild nicht
      if (!this.isAdminRevision && this.selectedAiEdit && !this.selectedAiEdit.released) {
        this.showReleaseOnRefinementDialog = true;
        return;
      }
      this.finishRevision();
    },
    async releaseAndFinishRevision() {
      if (this.selectedAiEdit) await this.setEditReleased(this.selectedAiEdit, true);
      this.showReleaseOnRefinementDialog = false;
      this.finishRevision();
    },
    finishRevision() {
      if (this.isAdminRevision) {
        this.markTask({
          uid: this.task.uid,
          revisionComment: this.revisionComment,
        });
        this.showRevisionDialog = false;
      } else {
        this.showRevisionDialog = false;
        this.$emit("refinement", {
          revisionComment: this.revisionComment,
          customOriginalImage: null,
          customFinalImage:
              this.localRefinementArtwork || this.refinementArtwork || null,
          currentConfig: this.updatedCustomizationConfigData,
        });
        this.$emit("approveProcessed");
      }
      this.isAdminRevision = false;
      // Review-Flow: Parent darf den nächsten Task öffnen
      this.$emit("taskCompleted", { autoNext: this.autoNext });
    },

    async urlToBlob(url) {
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error("Konnte Refinement-Bild nicht laden.");
      }
      return await res.blob();
    },

    async approveRefinement() {
      try {
        this.isSending = true;
        const fileName = this.getSafeFileName(this.task.orderName);
        const blob = await this.urlToBlob(this.localRefinementArtwork);
        const url = await this.uploadToStorage(
            blob,
            `templates/${currentMonthYear}/refinement/${fileName}`
        );

        const refinedAt = Date.now();
        // Verlauf: bestehendes (Legacy-)Refinement mit in die Versions-Liste
        // aufnehmen, dann die neue Version anhängen — arrayUnion dedupliziert
        const versionEntries = [];
        const prevUrl = this.customizationData?.refinementArtwork;
        if (prevUrl) {
          versionEntries.push({
            url: prevUrl,
            at: this.customizationData?.refinementArtworkAt || 0,
          });
        }
        versionEntries.push({ url, at: refinedAt });
        await setDoc(
            doc(db, `tasks`, this.task?.uid.toString()),
            {
              status: "REVIEW",
              reviewType: "REFINEMENT",
              editors: {
                REFINEMENT: this.currentUser.uid,
              },
              customizationData: {
                refinementArtwork: url,
                // Zeitstempel für die Neueste-Version-Erkennung (Edits↔Refinement)
                refinementArtworkAt: refinedAt,
                refinementVersions: arrayUnion(...versionEntries),
                savedConfig: this.updatedCustomizationConfigData,
              },
            },
            { merge: true }
        );
      } catch (e) {
        console.error(e);
        this.sendError = e && e.message ? e.message : String(e);
      } finally {
        this.isSending = false;
      }
    },

    getSafeFileName() {
      const shortId = this.generateShortId();
      const baseName = (this.orderName || "order").toString().trim();
      const safeOrderName =
          baseName
              .replace(/\s+/g, "-")
              .replace(/[^a-zA-Z0-9-_]/g, "")
              .toLowerCase() || "order";
      return `${safeOrderName}-${shortId}.jpg`;
    },

    /**
     * Druckdatei als Canvas rendern (Hintergrund → Artwork → Texte). Unverändert die Logik
     * des bisherigen Exports — der Server-Renderer (functions-upscaler/src/printFile.ts) ist
     * ein Port davon: Änderungen hier dort nachziehen.
     */
    async renderPrintCanvas() {
      function extractSize(str) {
        if (!str || typeof str !== "string") return null;
        const lastPart = str.split("x")?.[0]?.trim();
        const match = lastPart.match(/^(\d+)/);
        return match ? Number(match[1]) : null;
      }

      const size = extractSize(this.config.size);
      const maxSize = size && size > 45 ? 8000 : 4000;

      const orientation = this.effectiveOrientation || "portrait";
      const ratio = orientation === "landscape" ? 3 / 2 : 2 / 3;

      let width, height;
      if (orientation === "landscape") {
        width = maxSize;
        height = Math.round(width / ratio);
      } else {
        height = maxSize;
        width = Math.round(height * ratio);
      }

      const imgUrl =
          this.artworkDisplayUrl ||
          this.customizationData.artworkImageUrl ||
          this.customizationData.originalImage;

      if (!imgUrl) {
        throw new Error("No artwork image URL available.");
      }

      // Regionen-Regler der Upscale-Kette: hier wird EINMAL in voller Auflösung
      // zusammengesetzt (die Anzeige nutzt eine verkleinerte Vorschau)
      const img = await this.exportArtworkSource(imgUrl);

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");

      // Grundlage: gewählter Hintergrund-Layer (transparente Artworks) —
      // Farbe oder Full-Size-Textur — oder Weiß (Bestands-Tasks unverändert)
      await this.drawBackgroundLayer(ctx, width, height);

      this.renderArtworkToCanvas(ctx, img, width, height);
      this.renderTextsToCanvas(ctx, width, height);
      return canvas;
    },

    // SEND TO FULFILL
    async sendToFulfill(sendPreviewCustomer = false) {
      try {
        this.isSending = true;
        this.sendError = null;
        this.$emit("approveProcessed");

        // Server-Modus auf der Import-Karte: Der Server rendert UND importiert (derselbe Weg wie der
        // Auto-Import). Wir geben nur den Stand ab und machen sofort mit dem nächsten Task weiter.
        if (this.serverImport && this.printOnServer) {
          let payload = null;
          try {
            payload = { taskUid: this.task.uid, ...this.serverPrintPayload(false) };
          } catch (err) {
            // z. B. Bild liegt nur lokal vor → dieser Task wird unten lokal importiert
            this.serverPrintFallback = (err && err.message) || String(err);
          }
          if (payload) {
            let refinementUrl = null;
            if (this.localRefinementArtwork) {
              refinementUrl = await this.uploadToStorage(
                  await this.urlToBlob(this.localRefinementArtwork),
                  `templates/${currentMonthYear}/refinement/${this.getSafeFileName(this.task.orderName)}`
              );
            }
            this.$emit("serverImport", { payload, sendPreviewCustomer, refinementUrl, currentConfig: this.updatedCustomizationConfigData });
            this.$emit("taskCompleted", { autoNext: this.autoNext });
            return;
          }
        }

        // Druckdatei: auf dem Server (Standard) oder lokal im Browser — Schalter „Server / Lokal".
        // Scheitert der Server, liefert der Aufruf null, stellt den Task auf „Lokal" und wir rendern hier.
        const serverPrintUrl = await this.serverPrintUrlOrFallback();
        const blob = serverPrintUrl
            ? null
            : await this.canvasToBlob(await this.renderPrintCanvas(), "image/jpeg", 0.9);

        const fileName = this.getSafeFileName(this.task.orderName);
        const path = `templates/${currentMonthYear}/final/${fileName}`;
        const url = serverPrintUrl || (await this.uploadToStorage(blob, path));

        let urlRefinement = null;
        if (this.localRefinementArtwork) {
          const blobRefinement = await this.urlToBlob(
              this.localRefinementArtwork
          );
          urlRefinement = await this.uploadToStorage(
              blobRefinement,
              `templates/${currentMonthYear}/refinement/${fileName}`
          );
        }

        this.$emit("approve", {
          finalUrl: url,
          refinementUrl: urlRefinement,
          currentConfig: this.updatedCustomizationConfigData,
          sendPreviewCustomer: sendPreviewCustomer,
        });

        this.$emit("approved", url);
        // Review-Flow: Parent darf den nächsten Task öffnen
        this.$emit("taskCompleted", { autoNext: this.autoNext });
      } catch (err) {
        console.error(err);
        this.sendError = err && err.message ? err.message : String(err);
      } finally {
        this.isSending = false;
      }
    },

    /** Bild-URL einer Version der Versionsleiste (ORIGINAL, UPSCALE_RAW, DEFAULT, JOB_EDIT_i, AI_EDIT_i, REFINEMENT…) */
    variantUrl(key) {
      if (String(key).startsWith("REFINEMENT_V_")) {
        const index = Number(String(key).slice(13));
        return this.refinementVersionsList[index]?.url || null;
      }
      if (key === "REFINEMENT") {
        return this.effectiveRefinementArtwork;
      }
      if (String(key).startsWith("AI_EDIT_")) {
        const index = Number(String(key).slice(8));
        return this.aiEdits[index]?.url || null;
      }
      if (key === "ORIGINAL") {
        return (
            this.customizationData.artworkImageUrl ||
            this.customizationData.originalImage
        );
      }
      if (key === "UPSCALE_RAW") {
        return this.liveUpscaleRawImage || this.customizationData.upscaleRawImage || null;
      }
      // Neuester Stand (Detail-Pass bzw. letzter Edit) live aus dem Job — der übergebene upscaledUrls.DEFAULT ist ein Schnappschuss
      if (key === "DEFAULT" && this.liveUpscaledImage) {
        return this.liveUpscaledImage;
      }
      // Edit-Versionen der Upscale-Kette: Stand vor allen Edits bzw. nach Edit i (regionEdit.ts: edits[].previous/final)
      if (key === "DETAIL_PASS_BASE") {
        const first = (this.regionEdits || [])[0];
        return (first && first.previous && first.previous.final && first.previous.final.url) || this.customizationData.upscaledImage || null;
      }
      if (String(key).startsWith("JOB_EDIT_")) {
        const e = (this.regionEdits || [])[Number(String(key).slice(9))];
        return (e && e.final && e.final.url) || this.customizationData.upscaledImage || null;
      }
      const val = this.upscaledUrls[key];
      return typeof val === "object" && val ? val.url : val;
    },


    // --- Vergleichs-Dialog ---
    openCompareDialog() {
      this.resetCompareView();
      this.compareTab = "overview";
      this.ensureCompareVariant();
      this.compareDialog = true;
    },
    /**
     * Vorladen (CardsGrid ruft das für die nächsten Tasks auf): alle Bilder, die beim Öffnen
     * gebraucht werden, in den Browser-Cache holen — Artwork-Varianten, Originale, Vorschau,
     * Region-Ausschnitte der Upscale-Kette. Bei schlechter Verbindung öffnet der Editor dann flüssig.
     */
    preloadAssets() {
      const cd = this.customizationData || {};
      const urls = [
        this.liveUpscaledImage, this.liveUpscaleRawImage,
        cd.upscaledImage, cd.upscaleRawImage, cd.artworkImageUrl, cd.previewImg,
        cd.originalImage, cd.originalImage2, cd.refinementArtwork, this.refinementArtwork,
        ...(Array.isArray(cd.originalImages) ? cd.originalImages : []),
        ...(cd.aiEdits || []).map((e) => e && e.url),
        ...Object.values(this.upscaledUrls || {}).map((v) => (v && typeof v === "object" ? v.url : v)),
        ...(this.regionEdits || []).flatMap((e) => [e && e.final && e.final.url, e && e.previous && e.previous.final && e.previous.final.url]),
      ];
      for (const r of this.upscaleRegions || []) {
        const c = r.crops || {};
        urls.push(c.original, c.before, c.after, c.rendered, c.reference);
      }
      preloadImages(urls);
    },
    /** Aktive Version in einer horizontal scrollenden Leiste mittig sichtbar machen */
    scrollActiveVersionIntoView(container, smooth = true) {
      if (!container || !container.querySelector) return;
      const chip = container.querySelector(".active");
      if (!chip || container.scrollWidth <= container.clientWidth) return;
      const left = chip.offsetLeft - (container.clientWidth - chip.offsetWidth) / 2;
      container.scrollTo({ left: Math.max(0, left), behavior: smooth ? "smooth" : "auto" });
    },
    /** Links steht die im Editor gewählte Version; „Original" liegt in der Mitte → dann neueste Version zeigen */
    ensureCompareVariant() {
      const items = this.compareVersionItems.filter((i) => !i.arrow && !i.disabled);
      if (!items.length || items.some((i) => i.key === this.selectedVariant)) return;
      this.selectedVariant = items[items.length - 1].key;
    },
    upscaleScoreColor(score) {
      return scoreColor(score, this.upscaleScoreLimit);
    },
    /** Vergleich öffnen und auf eine Region der Upscale-Kette zoomen (Box in Promille des Artworks) */
    compareFocusRegion(region) {
      this.resetCompareView();
      this.ensureCompareVariant();
      this.compareDialog = true;
      const box = region && region.box;
      if (!box) return;
      this.$nextTick(() => {
        const pane = this.$refs.comparePaneArtwork;
        if (!pane || !pane.clientWidth || !pane.clientHeight) return;
        const paneW = pane.clientWidth;
        const paneH = pane.clientHeight;
        // Bild liegt per object-fit: contain mittig in der Spalte
        const ratio = this.imageAspectRatio || 2 / 3;
        const contentW = Math.min(paneW, paneH * ratio);
        const contentH = contentW / ratio;
        const cx = (box.x + box.width / 2) / 1000 - 0.5;
        const cy = (box.y + box.height / 2) / 1000 - 0.5;
        const fill = Math.max(((box.width / 1000) * contentW) / paneW, ((box.height / 1000) * contentH) / paneH);
        const zoom = Math.min(10, Math.max(1, 0.55 / Math.max(fill, 0.01)));
        this.compareZoom = zoom;
        this.compareOffset = { x: -zoom * cx * contentW, y: -zoom * cy * contentH };
      });
    },
    resetCompareView() {
      this.compareZoom = 1;
      this.compareOffset = { x: 0, y: 0 };
      this.compareOrigAdjust = { z: 1, x: 0, y: 0 };
      this.compareDrag = {
        active: false,
        target: "main",
        startX: 0,
        startY: 0,
        origX: 0,
        origY: 0,
      };
    },
    /** target 'main' = Master (Originale folgen), 'orig' = nur die Abweichung der Originale */
    compareOnWheel(e, target = "main") {
      const step = e.deltaY > 0 ? -0.1 : 0.1;
      if (target === "orig") {
        const total = this.compareZoom * this.compareOrigAdjust.z + step;
        const clamped = Math.min(Math.max(total, 0.5), 10);
        this.compareOrigAdjust = { ...this.compareOrigAdjust, z: clamped / this.compareZoom };
        return;
      }
      this.compareZoom = Math.min(Math.max(this.compareZoom + step, 0.5), 10);
    },
    compareOnMouseDown(e, target = "main") {
      if (e.button !== 0) return;
      const src = target === "orig" ? this.compareOrigAdjust : this.compareOffset;
      this.compareDrag = { active: true, target, startX: e.clientX, startY: e.clientY, origX: src.x, origY: src.y };
    },
    compareOnMouseMove(e) {
      if (!this.compareDrag.active) return;
      const dx = e.clientX - this.compareDrag.startX;
      const dy = e.clientY - this.compareDrag.startY;
      const x = this.compareDrag.origX + dx;
      const y = this.compareDrag.origY + dy;
      if (this.compareDrag.target === "orig") this.compareOrigAdjust = { ...this.compareOrigAdjust, x, y };
      else this.compareOffset = { x, y };
    },
    compareOnMouseUp() {
      this.compareDrag.active = false;
    },

    // --- Hintergrund-Layer (Farbe oder Textur) ---
    backgroundSwatchStyle(opt) {
      if (opt.value.startsWith("#")) return { backgroundColor: opt.value };
      return {
        backgroundImage: `url(${backgroundAssetUrl(opt.value, "thumbs")})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      };
    },
    // Malt den Hintergrund unter das transparente Artwork. Texturen kommen in
    // Full-Size und in gemischter Ausrichtung (2:3 ODER 3:2) — bei Bedarf wird
    // automatisch 90° gedreht, dann cover-beschnitten.
    /** Hintergrund-Layer NUR im inneren Bereich — das Passepartout bleibt weiß (wie Editor-Vorschau und Server-Druckdatei) */
    async drawBackgroundLayer(ctx, width, height) {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
      const bg = this.localBackground || "#ffffff";
      const matPx = (Math.min(width, height) * (this.localCanvas.mat || 0)) / 100;
      const innerW = width - matPx * 2;
      const innerH = height - matPx * 2;
      if (innerW <= 0 || innerH <= 0) return;
      if (bg.startsWith("#")) {
        ctx.fillStyle = bg;
        ctx.fillRect(matPx, matPx, innerW, innerH);
        return;
      }
      const img = await this.loadImage(backgroundAssetUrl(bg, "full"));
      const rotate = (img.naturalWidth > img.naturalHeight) !== (innerW > innerH);
      const effW = rotate ? img.naturalHeight : img.naturalWidth;
      const effH = rotate ? img.naturalWidth : img.naturalHeight;
      const scale = Math.max(innerW / effW, innerH / effH);
      const drawW = img.naturalWidth * scale;
      const drawH = img.naturalHeight * scale;
      ctx.save();
      if (matPx > 0) {
        ctx.beginPath();
        ctx.rect(matPx, matPx, innerW, innerH);
        ctx.clip();
      }
      ctx.translate(matPx + innerW / 2, matPx + innerH / 2);
      if (rotate) ctx.rotate(Math.PI / 2);
      ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
      ctx.restore();
    },

    // --- SAVE / CLOSE ---
    save() {
      this.$emit("save", {
        id: this.customizationData.id,
        configuration: {
          ...this.config,
          artworkCanvas: { ...this.localCanvas },
          textElements: this.localTextElements,
          orientation: this.effectiveOrientation,
          ...(this.hasBackgroundLayer
            ? {
                background:
                  this.localBackground && !this.localBackground.startsWith("#")
                    ? { type: "image", value: this.localBackground }
                    : { type: "color", value: this.localBackground || "#ffffff" },
              }
            : {}),
        },
        selectedUpscaleVariant: this.selectedVariant,
      });
      this.close();
    },
    close() {
      this.stopDrag();
      this.showRevisionDialog = false;
      this.isFrameDragOver = false;
      this.compareDialog = false;
      this.show = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.text-field-wrapper {
  position: relative;
}

.floating-actions {
  display: flex;
  gap: 6px;
  z-index: 10;
  background: transparent;
}

.live-action-container {
  margin-top: auto;
  margin-bottom: 12px;
  text-align: center;
  padding-top: 8px;
  border-top: 1px solid #eee;
  background: white;
}

.live-editor-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #f5f5f5;
  overflow: hidden;
}

.editor-body {
  display: flex;
  flex: 1;
  height: calc(100% - 48px);
  overflow: hidden;
}

/* LEFT SIDE */
.canvas-container {
  flex: 1;
  background-color: #333;
  position: relative;
  display: flex;
  justify-content: center;
  overflow: hidden;
  flex-direction: column;
  align-items: stretch;
}

.canvas-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  /* Platz für die Leiste unten (44px + 12px); die Pillen oben sitzen in den Ecken
     und blockieren nur bei breiten Rahmen (siehe frameContainerStyle) */
  padding: 12px 0 64px;
}

/* Schwebende Kopfzeile oben links */
.le-topbar {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 25;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px 0 5px;
  border-radius: 999px;
  background: rgba(30, 30, 34, 0.78);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);

  &__close {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    .v-icon {
      color: rgba(255, 255, 255, 0.85) !important;
    }
    &:hover {
      background: rgba(255, 255, 255, 0.14);
    }
  }
  &__flag {
    font-size: 16px;
    line-height: 1;
    flex: 0 0 auto;
  }
  &__name {
    font-size: 12.5px;
    font-weight: 700;
    color: #fff;
    white-space: nowrap;
  }
  &__meta {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.55);
    white-space: nowrap;
  }
}

/* Original/Übersetzung in der Kopfzeile der Feedback-Karte */
.le-orig-toggle {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 9px;
  margin-right: 4px;
  border: 0;
  border-radius: 999px;
  background: #eef0f3;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  color: rgba(0, 0, 0, 0.55);
  cursor: pointer;

  .v-icon {
    color: inherit !important;
  }
  &--on {
    background: #edeafa;
    color: #6a4fc4;
  }
}

.zoom-hint {
  position: absolute;
  top: 12px;
  right: 12px;
  height: 36px;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba(30, 30, 34, 0.78);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  z-index: 10;
  cursor: pointer;

  .v-icon {
    color: inherit !important;
  }
}

.print-frame {
  background-color: white;
  position: relative;
  overflow: hidden;
  cursor: grab;
  flex: 0 0 auto;
}

.print-frame:active {
  cursor: grabbing;
}

.print-frame--drag-over {
  outline: 2px dashed rgba(25, 118, 210, 0.9);
  outline-offset: -4px;
}

.v-btn-toggle {
  button {
    text-transform: none !important;
  }
}

.artwork-layer {
  display: block;
}

/* Regionen der Upscale-Kette: Rahmen + Score über dem Artwork */
.upscale-region-overlay {
  z-index: 2;
}
@keyframes region-pulse {
  0%, 100% { box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.6), 0 0 0 0 rgba(123, 92, 214, 0.7); }
  50% { box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.6), 0 0 0 10px rgba(123, 92, 214, 0); }
}
/* Markier-Ebene für Region-Edits: fängt die Maus, Rechteck in Prozent des Artworks */
.region-draw-layer {
  z-index: 3;
  pointer-events: auto !important;
  cursor: crosshair;
}
.region-draw-box {
  position: absolute;
  border: 2px dashed #7b5cd6;
  background: rgba(123, 92, 214, 0.12);
  pointer-events: none;
}
.upscale-region-box {
  position: absolute;
  border: 2px solid;
  border-radius: 3px;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.6);

  span {
    position: absolute;
    top: -9px;
    left: -2px;
    padding: 0 5px;
    border-radius: 4px;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    line-height: 15px;
    white-space: nowrap;
  }

  &--hidden {
    border-style: dashed;
  }
  &--custom {
    border: 2px solid #7b5cd6;
    box-shadow: 0 0 0 2px rgba(123, 92, 214, 0.25);
    pointer-events: none;
  }
  &--running {
    border-color: #7b5cd6;
    animation: region-pulse 1.2s ease-in-out infinite;

    span {
      background: #7b5cd6;
    }
  }
  &--hovered {
    box-shadow: 0 0 0 3px rgba(123, 92, 214, 0.75);
  }
  /* Das Overlay selbst lässt Maus-Ereignisse durch (Artwork bleibt verschiebbar) — nur die Rahmen sind klickbar */
  &--clickable {
    pointer-events: auto;
    cursor: zoom-in;

    &:hover {
      box-shadow: 0 0 0 3px rgba(123, 92, 214, 0.75);
    }
  }
}

/* Umschalter „Vorher / Nachher" oben mittig über dem Canvas */
.upscale-faces-toggle {
  position: absolute;
  top: 12px;
  right: 118px;
  z-index: 25;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border-radius: 999px;
  background: rgba(30, 30, 34, 0.78);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
  font-size: 12.5px;
  font-weight: 600;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  cursor: pointer;

  .v-icon {
    color: inherit !important;
  }
  &__count {
    padding: 0 7px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.16);
    color: #fff;
    font-size: 11px;
  }
}

/* Grid overlay */
.grid-lines {
  display: none;
  position: absolute;
  inset: 0;
  pointer-events: none;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background-image: linear-gradient(
          to right,
          rgba(0, 0, 0, 1) 1px,
          transparent 1px
  ),
  linear-gradient(to bottom, rgba(0, 0, 0, 1) 1px, transparent 1px);
  background-size: 33.3% 33.3%;
  z-index: 10;
}

/* Floating controls */
/* ===== Radikal-Pass: Settings-Formensprache (flach, grün, Pills) ===== */
.settings-sidebar {
  /* Typo-Grundlinie */
  font-size: 13px;

  /* Karten: weiß mit weichem Schatten auf grauem Grund, Kopfzeile mit getöntem Icon */
  .preview-box {
    text-align: left;
    align-items: stretch;
    padding: 10px 12px 12px;
    background: #fff;
    border: 0;
    border-radius: 14px !important;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06), 0 4px 14px rgba(16, 24, 40, 0.07);

    .text-caption {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      min-height: 24px;
      font-size: 12.5px !important;
      font-weight: 600;
      line-height: 1.2;
      letter-spacing: 0;
      text-transform: none;
      color: rgba(0, 0, 0, 0.82) !important;
      margin-bottom: 8px !important;

      > .v-icon:first-child {
        width: 24px;
        height: 24px;
        margin-right: 8px !important;
        border-radius: 8px;
        background: #eef0f3;
        color: rgba(0, 0, 0, 0.6) !important;
        font-size: 14px !important;
      }
      .v-btn {
        margin-left: 2px;
        color: rgba(0, 0, 0, 0.45);
      }
    }
    /* Kleine Kacheln (Originale): dezente Kopfzeile */
    &.preview-box-small .text-caption {
      min-height: 16px;
      font-size: 10.5px !important;
      color: rgba(0, 0, 0, 0.5) !important;
      margin-bottom: 4px !important;
    }
  }
  .le-card {
    margin-bottom: 10px;
  }
  /* Icon-Töne je Karte */
  .preview-box--note .text-caption > .v-icon:first-child {
    background: #fff3d6;
    color: #a86f00 !important;
  }
  .preview-box--good .text-caption > .v-icon:first-child {
    background: #e4f5eb;
    color: #2f7d52 !important;
  }
  .preview-box--bad .text-caption > .v-icon:first-child {
    background: #fde7e7;
    color: #c62828 !important;
  }
  .ai-edit-box .text-caption > .v-icon:first-child {
    background: #e4f5eb;
    color: #2f7d52 !important;
  }
  .ai-edit-box--region .text-caption > .v-icon:first-child {
    background: #edeafa;
    color: #6a4fc4 !important;
  }
  /* Knöpfe in Karten: Pillen */
  .preview-box ::v-deep .v-btn.v-size--small:not(.v-btn--icon):not(.v-btn--text) {
    border-radius: 999px;
    height: 34px;
    font-size: 12.5px;
    font-weight: 600;
    letter-spacing: 0.1px;
    text-transform: none;
  }

  /* Slider: gefüllter grüner Track, weißer Thumb mit grünem Ring */
  ::v-deep .v-slider__track-fill {
    background: #3f9d68 !important;
  }
  ::v-deep .v-slider__track-background {
    background: #e3e6ea !important;
  }
  ::v-deep .v-slider__thumb {
    width: 16px;
    height: 16px;
    left: -8px;
    background: #fff !important;
    border: 2.5px solid #3f9d68 !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
  ::v-deep .v-slider__thumb-container:hover .v-slider__thumb {
    transform: scale(1.1);
  }

  /* Switches: modern inset-artig */
  ::v-deep .v-input--switch__track {
    opacity: 1;
    color: #d3d7de;
  }
  ::v-deep .v-input--switch.v-input--is-label-active .v-input--switch__track {
    color: #3f9d68;
  }
  ::v-deep .v-input--switch.v-input--is-label-active .v-input--switch__thumb {
    color: #fff;
  }

  /* Buttons: Pills ohne Großbuchstaben */
  ::v-deep .v-btn {
    text-transform: none;
    letter-spacing: 0.1px;
    border-radius: 999px;
  }

  /* Panel-Header: kompakt, fett, moderner Chevron-Abstand */
  ::v-deep .v-expansion-panel-header {
    font-size: 13px;
    font-weight: 700;
    min-height: 44px;
  }
  ::v-deep .v-expansion-panel--active .border-panel,
  .border-panel.v-expansion-panel--active {
    border-color: rgba(63, 157, 104, 0.45) !important;
  }

  /* Beschriftungen über Slidern */
  .caption {
    font-size: 11px !important;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: rgba(0, 0, 0, 0.45) !important;
  }
}

.field-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 4px;
}

.le-section-title {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.2px;
  color: rgba(0, 0, 0, 0.85);
}

/* Fulfill: Icon-Pill links — Label fährt bei Hover aus; Armed = Warnzustand */
/* Versions-Timeline: Chips chronologisch alt → neu, Pfeile dazwischen */
.le-version-bar {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 3px;
  min-width: 0;
  flex: 1 1 auto;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  padding: 2px 0;

  &::-webkit-scrollbar {
    display: none;
  }
  > * {
    flex: 0 0 auto;
  }
}

/* Mail-Symbol oben rechts an der Version, die der Kunde als Vorschau bekommen hat */
.le-version-mail {
  position: absolute;
  top: -5px;
  right: -4px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #3f9d68;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 1.5px #fff;

  .v-icon {
    font-size: 9px !important;
    color: #fff !important;
  }
}
.compare-orig-switch .le-version-mail {
  box-shadow: 0 0 0 1.5px rgba(0, 0, 0, 0.55);
}

.le-version-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 11px;
  border: 1px solid #e3e1ea;
  border-radius: 999px;
  background: #f7f6fa;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.2px;
  color: rgba(0, 0, 0, 0.58);
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover:not(.disabled):not(.active) {
    background: #efedf6;
    border-color: #d5d0e4;
  }

  &.active {
    background: #edeafa;
    border-color: #7b5cd6;
    color: #6a4fc4;
    box-shadow: 0 1px 5px rgba(123, 92, 214, 0.28);
  }

  &.disabled {
    opacity: 0.45;
    cursor: default;
  }

  &.pending {
    cursor: default;
  }
}

.le-version-arrow {
  color: rgba(0, 0, 0, 0.28) !important;
  margin: 0 -1px;
  flex-shrink: 0;
}

.le-flag-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  min-width: 64px;
  height: 40px;
  padding: 0 11px;
  border: none;
  border-radius: 999px;
  background: #f0a92e;
  color: #fff;
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  transition: background 0.15s ease, box-shadow 0.15s ease;
  flex-shrink: 0;

  &:hover {
    box-shadow: 0 2px 8px rgba(240, 169, 46, 0.45);
  }

  &__label {
    max-width: 0;
    opacity: 0;
    font-size: 13px;
    font-weight: 700;
    overflow: hidden;
    transition: max-width 0.2s ease, opacity 0.2s ease, margin-left 0.2s ease;
  }

  &:hover .le-flag-btn__label {
    max-width: 120px;
    opacity: 1;
    margin-left: 8px;
  }
}

.le-fulfill-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center; /* Icon-only: mittig */
  gap: 0;
  min-width: 64px;
  height: 40px;
  padding: 0 11px;
  border: none;
  border-radius: 999px;
  background: #3f9d68;
  color: #fff;
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  transition: background 0.15s ease, box-shadow 0.15s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    box-shadow: 0 2px 8px rgba(63, 157, 104, 0.4);
  }

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }

  &__label {
    max-width: 0;
    opacity: 0;
    font-size: 13px;
    font-weight: 700;
    transition: max-width 0.25s ease, opacity 0.2s ease, margin 0.25s ease;
  }

  &:hover:not(:disabled) .le-fulfill-btn__label,
  &--armed .le-fulfill-btn__label {
    max-width: 180px;
    opacity: 1;
    margin-left: 8px;
  }

  &--armed {
    background: #e59b3c;
    animation: le-fulfill-pulse 1s ease-in-out infinite;
  }
}

@keyframes le-fulfill-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(229, 155, 60, 0.5); }
  50% { box-shadow: 0 0 0 6px rgba(229, 155, 60, 0); }
}

/* Vergleichs-Button: ruhige Karte mit Tastatur-Hinweis */
.le-compare-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  border-radius: 10px;
  padding: 7px 12px;
  font-size: 12.5px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.8);
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: rgba(63, 157, 104, 0.5);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  &__key {
    margin-left: auto;
    font-size: 11px;
    font-weight: 700;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-bottom-width: 2px;
    border-radius: 6px;
    padding: 1px 7px;
    color: rgba(0, 0, 0, 0.5);
  }
}

/* ===== Sidebar-Layout: Tabs oben, scrollender Inhalt, Aktionen sticky ===== */
.sidebar-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.sidebar-scroll {
  flex: 1;
  overflow-y: auto;
  padding-right: 4px;
  scrollbar-width: thin;
}

.sidebar-tabs {
  display: flex;
  gap: 4px;
  background: #e6e8ee;
  border-radius: 999px;
  padding: 4px;
  margin-bottom: 12px;
  flex-shrink: 0;

  &__tab {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border: none;
    background: transparent;
    border-radius: 999px;
    padding: 8px 10px;
    font-size: 13px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.55);
    cursor: pointer;
    transition: all 0.15s ease;

    .v-icon {
      color: inherit;
    }

    &--active {
      background: #fff;
      color: rgba(0, 0, 0, 0.85);
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
    }
  }

  /* Hinweis-Punkt: KI-Vorschlag vorhanden/aktiv */
  &__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #7b5cd6;
    box-shadow: 0 0 0 3px rgba(123, 92, 214, 0.2);
    flex-shrink: 0;
  }
}

.live-action-container {
  flex-shrink: 0;
  background: #fff;
  border-top: 1px solid rgba(0, 0, 0, 0.07);
  /* über die volle Sidebar-Breite: Innenabstand der Sidebar (.le-sidebar-pad) nach außen aufheben */
  margin: 8px -12px -12px -14px;
  padding: 10px 12px 12px 14px;
}


/* Karten-Look für Boxen & Panels */
.preview-box {
  border-radius: 12px !important;
  border: 1px solid rgba(0, 0, 0, 0.07);
  background: #fafbfc;
}

.border-panel {
  background: #fff !important;
  border: 1px solid rgba(0, 0, 0, 0.07) !important;
  border-radius: 12px !important;
}

.preview-img {
  border-radius: 8px;
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.015);
  }
}

.cloud-auto-chip {
  border: 1px solid rgba(0, 0, 0, 0.15);
  background: transparent;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 10px;
  color: rgba(0, 0, 0, 0.55);
  cursor: pointer;

  &--active {
    background: #e4f5eb;
    border-color: #3f9d68;
    color: #2f7d52;
  }
}

/* Kompound-Selektor: überschreibt sicher die spätere .color-swatch-Regel
   (width:100%/height:40px), die sonst wegen gleicher Spezifität + späterer
   Position gewinnt und das Swatch aus dem Panel schieben würde */
.color-swatch.color-swatch--small {
  width: 26px;
  height: 26px;
  border-radius: 4px; /* Quadrat */
  border: 1px solid rgba(0, 0, 0, 0.15);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
}

/* ===== Modernisierungs-Pass: Glas-Optik + weiche Karten ===== */
.preview-box--main {
  padding: 10px 12px 12px !important;
}

.order-note-text {
  font-size: 12.5px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
}

.ai-edit-box__refs {
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin: 6px 0 2px; min-height: 28px;
  border: 1px dashed rgba(0, 0, 0, 0.15); border-radius: 10px; padding: 4px 6px;
  &--empty { border-style: dashed; }
}
.ai-edit-box__ref {
  position: relative; width: 44px; height: 44px; border-radius: 8px; overflow: hidden; background: #f1f0f5; flex: 0 0 auto;
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
}
.ai-edit-box__refRemove {
  position: absolute; top: 0; right: 0; width: 16px; height: 16px; border: 0; border-radius: 0 8px 0 8px; background: rgba(0, 0, 0, 0.55); color: #fff; font-size: 12px; line-height: 16px; cursor: pointer; padding: 0;
}
.ai-edit-box__refAdd {
  display: inline-flex; align-items: center; gap: 4px; font-size: 11px; color: rgba(0, 0, 0, 0.5); cursor: pointer; padding: 2px 6px; border-radius: 999px;
  &:hover { background: rgba(0, 0, 0, 0.05); }
}
.ai-edit-box {
  text-align: left;

  ::v-deep .v-input {
    width: 100%;
    margin: 0;
  }

  ::v-deep .v-input__slot {
    min-height: 38px !important;
    padding: 0 12px !important;
    border-radius: 12px;
    box-shadow: none !important;
    border: 1px solid transparent;
    background: #f6f7f9 !important;
    transition: border-color 0.15s ease, background 0.15s ease;
  }
  ::v-deep .v-input--is-focused .v-input__slot {
    border-color: #7b5cd6;
    background: #fff !important;
  }

  ::v-deep textarea {
    font-size: 13px;
    line-height: 1.4;
    margin-top: 9px !important;
    margin-bottom: 9px !important;
  }

  &__count {
    background: #3f9d68;
    color: #fff;
    font-size: 10.5px;
    font-weight: 700;
    border-radius: 999px;
    padding: 1px 7px;
  }

  &__input ::v-deep .v-input__slot {
    background: #fff !important;
  }

  &__error {
    color: #b71c1c;
    font-size: 12px;
    margin-top: 6px;
  }
  &__status {
    color: #1f7a44;
    font-size: 12px;
    margin-top: 6px;
    text-align: center;
  }
  &__unclear {
    margin-top: 6px;
    padding: 6px 8px;
    border-radius: 8px;
    background: #fff7e6;
    color: #7a4b00;
    font-size: 11.5px;
    text-align: center;
  }

  /* Bereichs-Modus: lila wie der Rahmen im Bild */
  &--region {
    box-shadow: 0 0 0 2px rgba(123, 92, 214, 0.35), 0 4px 14px rgba(16, 24, 40, 0.07) !important;
  }
  &__regionRow {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
  }
  &__thumb {
    flex: 0 0 48px;
    width: 48px;
    height: 48px;
    border-radius: 8px;
    border: 2px solid #7b5cd6;
    background-color: #fff;
    background-repeat: no-repeat;
  }
  &__regionFields {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  &__close {
    flex: 0 0 auto;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: rgba(0, 0, 0, 0.5);
    cursor: pointer;

    &:hover {
      background: rgba(0, 0, 0, 0.06);
    }
  }
  &__select,
  &__name {
    padding: 3px 6px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 6px;
    font-size: 12px;
    background: #fff;
  }
  &__select,
  &__name {
    width: 100%;
  }
  &__sends {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
    margin-top: 6px;
  }
  &__sendLabel {
    font-size: 11px;
    color: rgba(0, 0, 0, 0.55);
    margin-right: 2px;
  }
  &__send {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 2px 8px;
    border-radius: 999px;
    border: 1px dashed rgba(0, 0, 0, 0.25);
    background: #fff;
    font: inherit;
    font-size: 11px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.55);
    cursor: pointer;

    .v-icon {
      color: inherit;
    }
    &--on {
      border-style: solid;
      border-color: #2e9e5b;
      color: #1f7a44;
      background: #eefaf3;
    }
    &--fixed {
      cursor: default;
    }
    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }
  /* Änderungen der gewählten Version */
  &__edits {
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px dashed #cdeeda;
  }
  &__edit {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px;
    margin-bottom: 6px;
    border-radius: 10px;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.08);
  }
  &__editThumb {
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
    border-radius: 8px;
    overflow: hidden;
    background: #f3f2f7;
    border: 2px solid #7b5cd6;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
    &--full {
      border-color: #2e9e5b;
    }
  }
  &__editBody {
    flex: 1;
    min-width: 0;
  }
  &__editTitle {
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  &__edits--running &__editThumb {
    background-repeat: no-repeat;
    border: 2px solid #7b5cd6;
  }
  &__editWarn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    margin-right: 4px;
    border-radius: 50%;
    background: #fde7e7;

    .v-icon {
      color: #c62828 !important;
      font-size: 11px !important;
    }
  }
  &__editScore {
    font-size: 11px;
    font-weight: 700;
    color: #1f7a44;
  }
  &__editText {
    font-size: 11.5px;
    color: rgba(0, 0, 0, 0.6);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__editDelete {
    flex: 0 0 auto;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #b71c1c;
    cursor: pointer;

    &:hover {
      background: rgba(183, 28, 28, 0.08);
    }
    &:disabled {
      opacity: 0.4;
      cursor: default;
    }
  }
  &__drawBtn {
    text-transform: none;
    letter-spacing: 0;
    height: 26px !important;
    font-size: 11.5px !important;
    justify-content: flex-start;
    color: rgba(0, 0, 0, 0.6) !important;

    &--on {
      color: #7b5cd6 !important;
    }
  }
  &__drawHint {
    font-size: 11.5px;
    color: #7b5cd6;
    text-align: center;
    margin-top: 2px;
  }
}

/* KI-Textvorschlag (Auto-Fix): Umschalter Aktuell / KI-Vorschlag */
.ai-proposal-box {
  background: #f4f0fd;
  border: 1px solid #e0d6f7;
  border-radius: 12px;
  padding: 12px 14px;

  &__head {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #7b5cd6;
    margin-bottom: 6px;
  }

  &__request {
    font-size: 12.5px;
    color: rgba(0, 0, 0, 0.6);
    font-style: italic;
    margin-bottom: 8px;
  }

  &__diff {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: 8px;
    font-size: 11.5px;
    color: rgba(0, 0, 0, 0.55);
  }
}

.ai-proposal-toggle {
  display: flex;
  background: #fff;
  border: 1px solid #e0d6f7;
  border-radius: 999px;
  padding: 3px;
  gap: 3px;

  &__btn {
    flex: 1;
    border: none;
    background: transparent;
    border-radius: 999px;
    font-size: 12.5px;
    font-weight: 600;
    padding: 5px 0;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.55);
    transition: background 0.15s ease, color 0.15s ease;

    &--active {
      background: #7b5cd6;
      color: #fff;
    }
  }
}

.floating-controls {
  /* fest am unteren Rand, der Rahmen bekommt die restliche Höhe */
  position: absolute;
  left: 50%;
  bottom: 12px;
  transform: translateX(-50%);
  width: 92%;
  max-width: 760px;
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 6px;
  height: 44px;
  padding: 0 8px;
  border-radius: 999px;
  background: rgba(30, 30, 34, 0.78);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  color: #fff;
  overflow: visible;

  /* Versions-Chips im dunklen Glas: bei vielen Versionen horizontal scrollen statt überlaufen */
  .le-version-bar {
    padding: 2px 0;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    mask-image: linear-gradient(to right, transparent 0, #000 10px, #000 calc(100% - 10px), transparent 100%);
  }
  .le-version-chip {
    height: 26px;
    background: rgba(255, 255, 255, 0.1);
    border-color: transparent;
    color: rgba(255, 255, 255, 0.78);

    &:hover:not(.disabled):not(.active) {
      background: rgba(255, 255, 255, 0.18);
      border-color: transparent;
    }
    &.active {
      background: #fff;
      border-color: #fff;
      color: #282828;
      box-shadow: none;
    }
  }
  .le-version-arrow {
    color: rgba(255, 255, 255, 0.45) !important;
  }
}
.fc-zoom {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex: 0 0 auto;

  /* Hover-Popover mit Regler, unsichtbare Brücke nach unten damit die Maus hinüberkommt */
  &__pop {
    position: absolute;
    left: 50%;
    bottom: 100%;
    transform: translateX(-50%) translateY(4px);
    padding-bottom: 10px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease, transform 0.15s ease;
  }
  &:hover &__pop {
    opacity: 1;
    pointer-events: auto;
    transform: translateX(-50%) translateY(0);
  }
  &__popInner {
    display: flex;
    align-items: center;
    height: 36px;
    padding: 0 14px;
    border-radius: 999px;
    background: rgba(30, 30, 34, 0.92);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.08);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  }
  &__range {
    -webkit-appearance: none;
    appearance: none;
    width: 200px;
    height: 4px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.25);
    outline: none;
    cursor: pointer;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #fff;
      border: 0;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
      cursor: grab;
    }
    &::-moz-range-thumb {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #fff;
      border: 0;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
      cursor: grab;
    }
  }

  &__btn {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    cursor: pointer;

    .v-icon {
      color: rgba(255, 255, 255, 0.85) !important;
    }
    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.14);
    }
    &:disabled {
      opacity: 0.35;
      cursor: default;
    }
  }
  &__val {
    min-width: 42px;
    text-align: center;
    font-size: 11.5px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.9);
    cursor: pointer;
    border-radius: 999px;
    padding: 2px 4px;

    &:hover {
      background: rgba(255, 255, 255, 0.14);
    }
  }
}
.fc-sep {
  width: 1px;
  height: 20px;
  background: rgba(255, 255, 255, 0.16);
  flex: 0 0 auto;
}
.fc-row {
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1 1 auto;

  &--versions {
    padding: 0 2px;
  }
}

/* RIGHT SIDE */
.settings-sidebar {
  width: 360px;
  background: #f3f4f7;
  border-left: 1px solid rgba(0, 0, 0, 0.06);
  z-index: 20;

  display: flex;
  flex-direction: column;
}

.sidebar-inner {
  min-height: 100%;
  position: relative;

  display: flex;
  flex-direction: column;
  flex: 1;
}
.le-sidebar-pad {
  padding: 12px 12px 12px 14px;
}

.sidebar-inner-settings {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 16px;
}

.preview-box {
  position: relative;
  background-color: #f5f5f5;
  border-radius: 4px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.preview-box--download-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 2;
}

.preview-box--warning {
  background-color: #ffd88d;
}

.preview-img {
  max-width: 100%;
  object-fit: contain;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  cursor: pointer;
}

.preview-img-main {
  max-height: 220px;
  align-self: center;
}

.preview-box-small {
  padding: 8px 8px 8px !important;
}

/* Originale als quadratische Kacheln, Bild füllt die Karte */
.preview-img-small {
  width: 100%;
  aspect-ratio: 1 / 1;
  max-height: none;
  object-fit: cover;
  box-shadow: none;
  border-radius: 6px;
}

/* Color picker */
.color-swatch {
  width: 100%;
  height: 40px;
  border-radius: 4px;
  border: 1px solid #ccc;
  cursor: pointer;
  position: relative;
}

.hidden-input {
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.border-panel {
  border: 1px solid #eee !important;
}

/* Text layer */
.text-layer {
  z-index: 20;
  position: relative; /* nötig, damit die SVG-Wolke dahinterliegt */
}

.text-cloud-svg {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 0;
  overflow: visible;
}

.text-measure {
  position: relative;
  display: inline-block;
  white-space: nowrap;
  z-index: 1;
}

/* Vergleichs-Dialog */
.compare-dialog-card {
  /* Dialog-Außenabstand (12px) ist eingerechnet, sonst ragt die Karte über den Bildschirm */
  height: calc(100vh - 24px);
  max-height: calc(100vh - 24px);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .compare-tabs {
    flex: 0 0 auto;
  }
  .v-card__text {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    padding: 0 12px 12px !important;
  }
  .v-card__text.compare-overview {
    overflow: hidden;
  }
}

/* Schalter Emojis: System / Unsere (über den Texten) */
.emoji-font-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 4px 0 10px;
  font-size: 11.5px;
  color: rgba(0, 0, 0, 0.6);

  &__label {
    font-weight: 700;
  }
}

/* Schalter Druckdatei: Server / Lokal (ganz unten) */
.print-mode-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
  font-size: 11.5px;
  color: rgba(0, 0, 0, 0.6);

  &__right {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
}
.print-mode {
  display: inline-flex;
  padding: 2px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.06);

  &__btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 12px;
    border-radius: 999px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.55);
    cursor: pointer;

    &--active {
      background: #fff;
      color: #282828;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
    }
    &:disabled {
      cursor: default;
    }
  }
  &__compare,
  &__next {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 999px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }
  &__compare--icon {
    padding: 2px 7px;
  }
  /* Schalter „Nächster": Pille mit Punkt, an = grün */
  &__next {
    color: rgba(0, 0, 0, 0.5);

    .print-mode__dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.2);
      margin-left: 2px;
      transition: background 0.15s ease;
    }
    &--on {
      border-color: #3f9d68;
      background: #e4f5eb;
      color: #2f7d52;

      .print-mode__dot {
        background: #3f9d68;
      }
    }
  }
  &__note {
    margin-top: 6px;
    font-size: 11.5px;
    color: #a3342c;

    &--info {
      color: rgba(0, 0, 0, 0.6);
    }
  }
}

/* Reiter im Vergleichsdialog */
.compare-tabs {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 48px;
  box-sizing: border-box;
  padding: 0 12px 0 16px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
  &__spacer {
    flex: 1;
  }
  &__title {
    font-size: 14px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.7);
  }
  > .v-btn {
    flex: 0 0 auto;
  }
}
.compare-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  border-radius: 999px;
  background: #f4f3f7;
  color: rgba(0, 0, 0, 0.65);
  font-size: 12.5px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;

  .v-icon {
    color: inherit !important;
  }
  &:hover {
    background: #edeafa;
  }
  &--active {
    background: #7b5cd6;
    color: #fff;

    &:hover {
      background: #7b5cd6;
    }
  }
  &--ghost {
    background: transparent;
    border: 1px solid rgba(0, 0, 0, 0.15);
  }
  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    box-shadow: 0 0 0 1.5px rgba(255, 255, 255, 0.9);
  }
}

/* Regionen-Vergleich: der Karteninhalt scrollt über die volle Dialoghöhe, eine Region je Zeile */
.compare-regions {
  height: calc(100vh - 24px - 48px);
  overflow-y: auto !important;
  scrollbar-width: thin;
}
.compare-faces {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 2px 0 8px;
  max-width: 1400px;
  margin: 0 auto;

  > * {
    width: 100%;
    flex: 0 0 auto;
  }
  /* eine einzelne Region: Bilder so groß wie möglich, aber nicht übers Format hinaus */
  &--single {
    max-width: none;
  }
  &--single > * {
    max-width: min(100%, 150vh);
  }
}

.compare-dialog-container {
  display: flex;
  flex-direction: row;
  gap: 8px;
  /* feste Höhe: Dialog (100vh − 24px Rand) − Reiterzeile 48px − Abstände 20px */
  height: calc(100vh - 92px);

  /* Druckdatei-Vergleich hat eine Textzeile darunter */
  .compare-print & {
    height: calc(100vh - 132px);
  }
  flex: 0 0 auto;
  min-height: 0;
  cursor: grab;
}

.compare-dialog-container:active {
  cursor: grabbing;
}

.compare-pane {
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  background: #0b0b0c;
  border-radius: 12px;
  overflow: hidden;
}

.compare-pane-label {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  pointer-events: none;
}

/* Kundenansicht ein-/ausblenden: unten rechts im linken Bild */
.compare-pane-toggle {
  position: absolute;
  right: 10px;
  bottom: 10px;
  z-index: 3;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  cursor: pointer;

  .v-icon {
    color: rgba(255, 255, 255, 0.85) !important;
  }
  &:hover {
    background: rgba(0, 0, 0, 0.75);
  }
  &.active .v-icon {
    color: #fff !important;
  }
}

.compare-pane-inner {
  flex: 1 1 0;
  min-height: 0;
  position: relative;
  overflow: hidden;
}

/* absolut, damit die natürliche Bildgröße die Rahmenhöhe nicht sprengt */
.compare-dialog-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  pointer-events: none;
}

/* Umschalter der Originale im Foto-Rahmen */
.compare-orig-switch {
  position: absolute;
  left: 50%;
  bottom: 10px;
  transform: translateX(-50%);
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  max-width: calc(100% - 96px);
  gap: 4px;
  padding: 3px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  cursor: default;

  button {
    min-width: 28px;
    height: 24px;
    padding: 0 9px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.8);
    cursor: pointer;

    &:hover {
      background: rgba(255, 255, 255, 0.18);
    }
    &.active {
      background: #fff;
      color: #6a4fc4;
    }
    &.disabled {
      opacity: 0.4;
      cursor: default;
    }
  }

  &__arrow {
    color: rgba(255, 255, 255, 0.6) !important;
    align-self: center;
    flex: 0 0 auto;
  }

  /* Versionsverlauf: eine Zeile, bei vielen Versionen horizontal scrollen */
  &--scroll {
    flex-wrap: nowrap;
    justify-content: flex-start;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    cursor: default;
    padding-top: 6px;

    &::-webkit-scrollbar {
      display: none;
    }
    button {
      flex: 0 0 auto;
      position: relative;
    }
  }
}
.bg-layer-picker {
  &__row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__swatch {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    border: 2px solid rgba(0, 0, 0, 0.15);
    cursor: pointer;
    transition: border-color 0.12s, transform 0.12s;

    &:hover { transform: scale(1.06); }

    &--selected {
      border-color: #282828;
      box-shadow: 0 0 0 2px rgba(40, 40, 40, 0.2);
    }

    &--custom {
      position: relative;
      display: inline-block;
      background: conic-gradient(#d98f82, #e3c77a, #a9cbb4, #a9c3d3, #b7b9b5, #d98f82);
    }
  }

  &__colorinput {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }
}
</style>

<style lang="scss">
/* Vergleichsdialog: Vuetify setzt `.v-dialog:not(.v-dialog--fullscreen) { max-height: 90% }`
   (gleiche Spezifität, später geladen) → hier höhere Spezifität + !important, sonst wird die
   Karte bei 90 % abgeschnitten */
.v-dialog__content .v-dialog.compare-dialog:not(.v-dialog--fullscreen) {
  margin: 12px !important;
  max-height: calc(100vh - 24px) !important;
  height: calc(100vh - 24px);
  overflow: hidden !important;
}
</style>
